import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePaletteCommands } from '../context/CommandPaletteContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { getCurrencySymbol } from '../lib/currency';

import logger from '../lib/logger';
import { computeEstimateProgress } from '../components/WorkspaceSubNav';
import { exportScopePack } from '../lib/factsheetPdf';
import {
  FolderOpen, Search, Clock, DollarSign, Briefcase,
  ChevronLeft, ChevronRight, Loader2, MessageSquare,
  CheckCircle2, AlertCircle, Send, FileEdit, BarChart3,
  Building2, Activity, Filter, Eye, Square, CheckSquare, ArrowUpDown, MoreHorizontal,
  X, Play, RotateCcw, Users, Sparkles, LayoutGrid, List, Star, StarOff, Pin, CalendarDays,
  Archive, ArchiveRestore, Trash2, FileText, FileDown, ArrowRight, Bookmark, Plus,
  Tag, AlertTriangle, UserCircle2, Headset, Paperclip, TrendingDown, Lock, Package, Flag,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import EstimateStatusSelect, { STATUS_GROUPS, statusLabel } from '../components/EstimateStatusSelect';
import DealStageSelect, { DEAL_STAGES } from '../components/DealStageSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import StatusOverrideAuditFeed from '../components/StatusOverrideAuditFeed';
import { SlipRollupCard } from '../components/planner/SlipRollupCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compact a redundant suffix that pile up when users clone via "Save as
// What-If" repeatedly: "Foo (What-If) (What-If) (What-If)" → "Foo (What-If ×3)".
// Pattern is intentionally narrow: only collapses immediately-trailing
// duplicates of the same parenthesised tag.
function dedupeName(raw) {
  if (!raw || typeof raw !== 'string') return raw;
  // Match a trailing run of identical "(...)" blocks (≥2 occurrences).
  const m = raw.match(/^(.*?)\s*(\(([^()]+)\))(?:\s*\(\3\))+\s*$/);
  if (!m) return raw;
  const base = m[1].trimEnd();
  const tag = m[3].trim();
  // Count occurrences of "(<tag>)" at the tail.
  const tail = raw.slice(base.length);
  const count = (tail.match(new RegExp(`\\(${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\)`, 'g')) || []).length;
  if (count < 2) return raw;
  return `${base} (${tag} ×${count})`;
}

const VIEW_KEY = 'workspaces_view_mode';

/**
 * Resume-where-you-left-off router. Given a list-view estimate doc
 * returned by `/api/estimates`, pick the natural next step in the
 * Customer → Requirements → Conditions → Estimate → Planner → Full
 * Report → Quote flow and return its path.
 *
 * "Empty" tabs win in flow order. If every actionable tab is complete
 * we fall back to /workspace/{id}/overview (the Full Report — the
 * natural sign-off destination). Conditions is intentionally skipped
 * from the next-step calculation: legacy estimates without conditions
 * would otherwise route everyone to a migration prompt, which would
 * frustrate the 95% case where users just want to look at the deal.
 * Migration remains one click away from the sub-nav itself.
 */
const RESUME_FLOW = [
  { key: 'requirements', label: 'Requirements', getPath: (id) => `/estimator/${id}/requirements` },
  { key: 'estimate',     label: 'Estimate',     getPath: (id) => `/estimator?load=${id}` },
  { key: 'planner',      label: 'Planner',      getPath: (id) => `/workspace/${id}/planner` },
  { key: 'overview',     label: 'Full Report',  getPath: (id) => `/workspace/${id}/overview` },
  { key: 'quote',        label: 'Quote',        getPath: (id) => `/workspace/${id}/quote` },
];

export function getResumeStep(estimate) {
  if (!estimate?.id) return null;
  const id = estimate.id;
  const progress = computeEstimateProgress(estimate);
  // First not-yet-complete step wins. Partial states are prioritised over
  // empty states later in the flow — finishing what you started beats
  // skipping ahead to something brand new.
  for (const step of RESUME_FLOW) {
    const state = progress[step.key];
    if (state === 'empty' || state === 'partial') {
      return { key: step.key, label: step.label, path: step.getPath(id), state };
    }
  }
  // All actionable steps complete — land on the Full Report as the
  // sign-off destination.
  return { key: 'overview', label: 'Full Report', path: `/workspace/${id}/overview`, state: 'complete' };
}

export function getResumePath(estimate) {
  return getResumeStep(estimate)?.path || '/workspaces';
}

/**
 * Small inline "Resumes at: Planner →" hint rendered on each workspace
 * row. Two lines:
 *   • Line 1: destination label (cyan if next step is empty, emerald if
 *     all flow steps are complete and Open will land on Full Report).
 *   • Line 2: 4-segment mini progress bar (Requirements · Estimate ·
 *     Planner · Quote) so reps can tell brand-new from mid-deal at a
 *     glance.
 *
 * Reads `getResumeStep(estimate)` + `computeEstimateProgress(estimate)`
 * so chip label, segments, and click destination stay in lockstep. Chip
 * is a `<span role="button">` (not a `<button>`) because it's often
 * nested inside the row-level workspace-name button — switching to a
 * span avoids nested-button HTML while keeping it keyboard-actionable.
 */
const SEGMENT_STEPS = [
  { key: 'requirements', label: 'Reqs' },
  { key: 'estimate',     label: 'Est'  },
  { key: 'planner',      label: 'Plan' },
  { key: 'quote',        label: 'Quote' },
];
const SEGMENT_COLOR = {
  complete: 'bg-emerald-500',
  partial:  'bg-amber-400',
  empty:    'bg-slate-200',
};

function ResumeChip({ estimate, compact = false }) {
  const navigate = useNavigate();
  const step = getResumeStep(estimate);
  if (!step) return null;
  const isComplete = step.state === 'complete';
  const progress = computeEstimateProgress(estimate);
  const cls = isComplete
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
    : 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100';
  const completedSegments = SEGMENT_STEPS.filter(s => progress[s.key] === 'complete').length;
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); navigate(step.path); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          navigate(step.path);
        }
      }}
      className={`inline-flex flex-col gap-0.5 px-1.5 py-0.5 rounded-md border cursor-pointer transition-colors ${cls} ${compact ? 'ml-1' : ''}`}
      title={isComplete
        ? `All ${SEGMENT_STEPS.length} flow steps complete — clicking lands on ${step.label}`
        : `${completedSegments}/${SEGMENT_STEPS.length} steps complete — clicking lands on ${step.label} (next empty step)`}
      data-testid={`resume-chip-${estimate.id}`}
    >
      <span className="inline-flex items-center gap-1 text-[10px] leading-none">
        <span className="text-slate-400">Resumes at</span>
        <span className="font-semibold">{step.label}</span>
        <ArrowRight className="w-2.5 h-2.5" />
      </span>
      <span
        className="flex items-center gap-px"
        data-testid={`resume-chip-progress-${estimate.id}`}
        aria-label={`Progress: ${completedSegments} of ${SEGMENT_STEPS.length} steps complete`}
      >
        {SEGMENT_STEPS.map(seg => {
          const state = progress[seg.key] || 'empty';
          return (
            <span
              key={seg.key}
              className={`h-1 w-3 rounded-sm ${SEGMENT_COLOR[state]}`}
              title={`${seg.label}: ${state}`}
              data-testid={`resume-chip-seg-${estimate.id}-${seg.key}`}
              data-state={state}
            />
          );
        })}
      </span>
    </span>
  );
}
// Security audit (Feb 2026 · Batch 1): this key only ever holds the
// literal string 'grid' or 'list' — a UI display preference. No PII,
// no auth state. Reader is shape-checked (only accepts those two
// values). Safe to use localStorage rather than httpOnly cookie.
function getInitialView() {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === 'grid' ? 'grid' : 'list';
  } catch (err) {
    // localStorage can throw in private/strict-cookie contexts —
    // default to list view rather than crash render.
    return 'list';
  }
}

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-200', dot: 'bg-slate-400', icon: FileEdit },
  in_review: { label: 'In Review', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400', icon: Send },
  approved: { label: 'Approved', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400', icon: CheckCircle2 },
  changes_requested: { label: 'Changes Req.', color: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-400', icon: AlertCircle },
};

const FILTER_TABS = [
  { id: '', label: 'All' },
  { id: 'draft', label: 'Draft' },
  { id: 'in_review', label: 'In Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'changes_requested', label: 'Changes Req.' },
  { id: 'completed', label: 'Completed' },
];

const WS_SEGMENT_OPTIONS = [
  { id: 'implementation', label: 'Implementation' },
  { id: 'customer', label: 'Customer Project' },
  { id: 'rnd', label: 'Seertech R&D' },
];

const WS_SEGMENT_TONE = {
  implementation: 'bg-sky-50 text-sky-700 border-sky-200',
  customer: 'bg-amber-50 text-amber-700 border-amber-200',
  rnd: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};
const WS_SEGMENT_SHORT = { implementation: 'Impl', customer: 'Cust', rnd: 'R&D' };

// Mirrors the Portfolio Overview filter popover structure (Type / Workflow / Lifecycle).
const WS_FILTER_GROUPS = [
  { name: 'Type', type: 'segment', options: WS_SEGMENT_OPTIONS },
  {
    name: 'Workflow',
    type: 'status',
    options: [
      { id: 'draft', label: 'Draft' },
      { id: 'in_review', label: 'In Review' },
      { id: 'approved', label: 'Approved' },
      { id: 'changes_requested', label: 'Changes Req.' },
    ],
  },
  {
    name: 'Lifecycle',
    type: 'status',
    options: [
      { id: 'confirmed', label: 'Confirmed' },
      { id: 'won', label: 'Won' },
      { id: 'lost', label: 'Lost' },
      { id: 'completed', label: 'Completed' },
    ],
  },
];

const SORT_OPTIONS = [
  { id: 'name', label: 'Name' },
  { id: 'created', label: 'Date Created' },
  { id: 'commences', label: 'Date Project Commences' },
  { id: 'effort', label: 'Effort' },
  { id: 'cost', label: 'Cost' },
  { id: 'product_tco', label: 'Product TCO' },
];

function timeAgo(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function ApprovalBar({ approval }) {
  const { total, approved, changes_requested, pending } = approval;
  if (total === 0) return null;
  const approvedPct = (approved / total) * 100;
  const changesPct = (changes_requested / total) * 100;
  const pendingPct = (pending / total) * 100;
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[9px] text-slate-500 mb-1">
        <span>Approvals</span>
        <span>{approved}/{total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden flex">
        {approvedPct > 0 && <div className="bg-emerald-400 h-full transition-all" style={{ width: `${approvedPct}%` }} />}
        {changesPct > 0 && <div className="bg-rose-400 h-full transition-all" style={{ width: `${changesPct}%` }} />}
        {pendingPct > 0 && <div className="bg-amber-300 h-full transition-all" style={{ width: `${pendingPct}%` }} />}
      </div>
    </div>
  );
}

// ---- Saved Views Menu (v260.32) ----
// Tiny "Views" dropdown next to the search bar. Lets reps save the
// current URL-filter state under a memorable name and one-click back to
// it later. Backing store: `user.preferences.saved_workspace_views`
// persisted via PUT /api/auth/preferences (capped at 20 entries by the
// backend; name max 60 chars, search max 500 chars).
function SavedViewsMenu({ onApply, currentSearch }) {
  const { user, checkAuth } = useAuth();
  const savedViews = user?.preferences?.saved_workspace_views || [];
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [nameInput, setNameInput] = useState('');

  const persistViews = useCallback(async (next) => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/auth/preferences`,
        { saved_workspace_views: next },
        { withCredentials: true },
      );
      if (checkAuth) await checkAuth();
    } catch (err) {
      toast.error('Could not update saved views');
      logger.warn('save-views: persist failed', err?.message);
    } finally {
      setSaving(false);
    }
  }, [checkAuth]);

  const handleSave = async () => {
    const name = nameInput.trim().slice(0, 60);
    if (!name) {
      toast.error('Give the view a name first');
      return;
    }
    const entry = { name, search: currentSearch || '' };
    const next = [entry, ...savedViews.filter(v => v.name !== name)].slice(0, 20);
    await persistViews(next);
    setNameInput('');
    setShowSaveForm(false);
    toast.success(`Saved view: ${name}`);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    const next = savedViews.filter(v => v.id !== id);
    await persistViews(next);
    toast.success('View removed');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-xs gap-1.5"
          data-testid="saved-views-trigger"
          title="Saved views — bookmark this triage filter set"
        >
          <Bookmark className="w-3.5 h-3.5" />
          Views
          {savedViews.length > 0 && (
            <span
              className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-cyan-100 text-cyan-700 text-[9px] font-bold flex items-center justify-center"
              data-testid="saved-views-count"
            >
              {savedViews.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        data-testid="saved-views-popover"
      >
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
            Saved Views
          </span>
          {!showSaveForm && (
            <button
              onClick={() => { setNameInput(''); setShowSaveForm(true); }}
              className="flex items-center gap-1 text-[10px] text-cyan-700 hover:text-cyan-900 transition-colors"
              data-testid="saved-views-add-btn"
            >
              <Plus className="w-3 h-3" /> Save current
            </button>
          )}
        </div>

        {showSaveForm && (
          <div className="px-3 py-2 border-b border-slate-100 space-y-2" data-testid="saved-views-form">
            <Input
              placeholder='View name (e.g. "My open deals waiting on Requirements")'
              value={nameInput}
              maxLength={60}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              className="text-xs"
              autoFocus
              data-testid="saved-views-name-input"
            />
            <div className="text-[10px] text-slate-400 truncate" title={currentSearch || '(no filters — base /workspaces view)'}>
              URL: ?{currentSearch || '(empty — base view)'}
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="text-[10px] h-6 px-2"
                onClick={() => { setShowSaveForm(false); setNameInput(''); }}
                data-testid="saved-views-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="text-[10px] h-6 px-2 bg-cyan-600 hover:bg-cyan-700 text-white"
                disabled={saving || !nameInput.trim()}
                onClick={handleSave}
                data-testid="saved-views-save-btn"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        )}

        {savedViews.length === 0 ? (
          <div
            className="px-3 py-6 text-center text-xs text-slate-400"
            data-testid="saved-views-empty"
          >
            No saved views yet.<br />
            Filter, sort, or page the dashboard, then click <strong>Save current</strong> to bookmark this triage layout.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {savedViews.map((v) => (
              <li
                key={v.id}
                className="px-3 py-2 hover:bg-slate-50 transition-colors group flex items-center gap-2 cursor-pointer"
                onClick={() => { onApply(v); setOpen(false); }}
                data-testid={`saved-view-${v.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-[#0F2040] truncate" title={v.name}>
                    {v.name}
                  </div>
                  <div className="text-[10px] text-slate-400 truncate" title={`?${v.search}`}>
                    ?{v.search || '(base view)'}
                  </div>
                </div>
                <button
                  onClick={(e) => handleDelete(e, v.id)}
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-all"
                  title="Remove this saved view"
                  data-testid={`saved-view-delete-${v.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}


// ---- Bulk Send Modal ----
function BulkSendModal({ selectedIds, workspaces, onClose, onDone }) {
  const [mode, setMode] = useState('shared'); // 'shared' | 'individual'
  const [sharedRecipients, setSharedRecipients] = useState([{ email: '', name: '', role: '' }]);
  const [perEstimate, setPerEstimate] = useState({});
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('LMS Implementation Business Case');
  const [sending, setSending] = useState(false);

  const selectedWs = workspaces.filter(w => selectedIds.has(w.id));

  // Init per-estimate
  useEffect(() => {
    const init = {};
    selectedWs.forEach(w => { init[w.id] = [{ email: '', name: '', role: '' }]; });
    setPerEstimate(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds]);

  const addSharedRecipient = () => setSharedRecipients(prev => [...prev, { email: '', name: '', role: '' }]);
  const updateShared = (i, field, val) => setSharedRecipients(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const removeShared = (i) => setSharedRecipients(prev => prev.filter((_, idx) => idx !== i));

  const addPerEstRecipient = (estId) => setPerEstimate(prev => ({ ...prev, [estId]: [...(prev[estId] || []), { email: '', name: '', role: '' }] }));
  const updatePerEst = (estId, i, field, val) => setPerEstimate(prev => ({
    ...prev,
    [estId]: (prev[estId] || []).map((r, idx) => idx === i ? { ...r, [field]: val } : r)
  }));

  const handleSend = async () => {
    setSending(true);
    try {
      const body = {
        estimate_ids: [...selectedIds],
        subject,
        message,
      };
      if (mode === 'shared') {
        body.shared_recipients = sharedRecipients.filter(r => r.email);
      } else {
        body.per_estimate_recipients = {};
        Object.entries(perEstimate).forEach(([id, recs]) => {
          const valid = recs.filter(r => r.email);
          if (valid.length) body.per_estimate_recipients[id] = valid;
        });
      }
      const res = await axios.post(`${API_URL}/api/workspaces/bulk-send`, body, { withCredentials: true });
      toast.success(`Sent to ${res.data.sent} recipient(s) across ${selectedIds.size} estimate(s)`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const hasValidRecipients = mode === 'shared'
    ? sharedRecipients.some(r => r.email)
    : Object.values(perEstimate).some(recs => recs.some(r => r.email));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="bulk-send-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4 text-[#4ABFCF]" />
            <h2 className="text-sm font-semibold text-[#0F2040]">Bulk Send Quotes</h2>
            <Badge variant="secondary" className="text-[9px]">{selectedIds.size} selected</Badge>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setMode('shared')}
              className={`flex-1 p-2 rounded-lg border text-xs font-medium transition-all ${mode === 'shared' ? 'border-[#4ABFCF] bg-[#4ABFCF]/5 text-[#0F2040]' : 'border-slate-200 text-slate-500'}`}
              data-testid="mode-shared"
            >
              <Users className="w-3.5 h-3.5 mx-auto mb-1" />
              Same recipients for all
            </button>
            <button
              onClick={() => setMode('individual')}
              className={`flex-1 p-2 rounded-lg border text-xs font-medium transition-all ${mode === 'individual' ? 'border-[#4ABFCF] bg-[#4ABFCF]/5 text-[#0F2040]' : 'border-slate-200 text-slate-500'}`}
              data-testid="mode-individual"
            >
              <FileEdit className="w-3.5 h-3.5 mx-auto mb-1" />
              Individual per estimate
            </button>
          </div>

          {/* Subject */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium mb-1 block">Subject</label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} className="text-xs" data-testid="send-subject" />
          </div>

          {/* Message */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium mb-1 block">Message (optional)</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-md px-3 py-2 h-16 resize-none focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
              placeholder="Optional message to include in the email..."
              data-testid="send-message"
            />
          </div>

          {/* Shared recipients */}
          {mode === 'shared' && (
            <div>
              <label className="text-[10px] text-slate-500 font-medium mb-2 block">Recipients</label>
              {sharedRecipients.map((r, i) => (
                <div key={`shared-${i}-${r.email || ''}`} className="flex gap-2 mb-2 items-center">
                  <Input value={r.email} onChange={e => updateShared(i, 'email', e.target.value)} placeholder="Email" className="text-xs flex-1" data-testid={`shared-email-${i}`} />
                  <Input value={r.name} onChange={e => updateShared(i, 'name', e.target.value)} placeholder="Name" className="text-xs w-28" />
                  <Input value={r.role} onChange={e => updateShared(i, 'role', e.target.value)} placeholder="Role" className="text-xs w-24" />
                  {sharedRecipients.length > 1 && (
                    <button onClick={() => removeShared(i)} className="text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              ))}
              <button onClick={addSharedRecipient} className="text-[10px] text-[#4ABFCF] hover:underline">+ Add recipient</button>
            </div>
          )}

          {/* Per-estimate recipients */}
          {mode === 'individual' && selectedWs.map(ws => (
            <div key={ws.id} className="border border-slate-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-[#0F2040] mb-2">{ws.estimate_name}</p>
              {(perEstimate[ws.id] || []).map((r, i) => (
                <div key={`per-${ws.id}-${i}-${r.email || ''}`} className="flex gap-2 mb-2 items-center">
                  <Input value={r.email} onChange={e => updatePerEst(ws.id, i, 'email', e.target.value)} placeholder="Email" className="text-xs flex-1" data-testid={`per-email-${ws.id}-${i}`} />
                  <Input value={r.name} onChange={e => updatePerEst(ws.id, i, 'name', e.target.value)} placeholder="Name" className="text-xs w-28" />
                  <Input value={r.role} onChange={e => updatePerEst(ws.id, i, 'role', e.target.value)} placeholder="Role" className="text-xs w-24" />
                </div>
              ))}
              <button onClick={() => addPerEstRecipient(ws.id)} className="text-[10px] text-[#4ABFCF] hover:underline">+ Add recipient</button>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={sending || !hasValidRecipients}
            className="bg-[#0F2040] hover:bg-[#0F2040]/90"
            data-testid="send-confirm-btn"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Send {selectedIds.size > 1 ? `${selectedIds.size} Quotes` : 'Quote'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Simulate Modal ----
function SimulateModal({ selectedIds, onClose, onDone }) {
  const [targetStatus, setTargetStatus] = useState('mixed');
  const [stakeholderCount, setStakeholderCount] = useState(3);
  const [simulating, setSimulating] = useState(false);

  const SIMULATE_OPTIONS = [
    { id: 'in_review', label: 'In Review', desc: 'All stakeholders pending', color: 'text-amber-600', icon: Send },
    { id: 'approved', label: 'Approved', desc: 'All stakeholders approve', color: 'text-emerald-600', icon: CheckCircle2 },
    { id: 'changes_requested', label: 'Changes Requested', desc: 'All request changes', color: 'text-rose-600', icon: AlertCircle },
    { id: 'mixed', label: 'Mixed', desc: '1 approved, 1 changes, rest pending', color: 'text-violet-600', icon: Sparkles },
  ];

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      await axios.post(`${API_URL}/api/workspaces/simulate`, {
        estimate_ids: [...selectedIds],
        target_status: targetStatus,
        stakeholder_count: stakeholderCount,
      }, { withCredentials: true });
      toast.success(`Simulated "${targetStatus.replace('_', ' ')}" for ${selectedIds.size} estimate(s)`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="simulate-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Play className="w-4 h-4 text-violet-500" />
            <h2 className="text-sm font-semibold text-[#0F2040]">Simulate Lifecycle</h2>
            <Badge variant="secondary" className="text-[9px]">{selectedIds.size} selected</Badge>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-[10px] text-slate-500">Create simulated stakeholder responses to see how the dashboard reflects different lifecycle stages. No real emails are sent.</p>

          <div className="grid grid-cols-2 gap-2">
            {SIMULATE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => setTargetStatus(opt.id)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  targetStatus === opt.id ? 'border-[#4ABFCF] bg-[#4ABFCF]/5 ring-1 ring-[#4ABFCF]' : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`sim-option-${opt.id}`}
              >
                <opt.icon className={`w-4 h-4 ${opt.color} mb-1`} />
                <p className="text-xs font-semibold text-[#0F2040]">{opt.label}</p>
                <p className="text-[9px] text-slate-500 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>

          <div>
            <label className="text-[10px] text-slate-500 font-medium mb-1 block">Stakeholders per estimate</label>
            <div className="flex items-center gap-2">
              {[2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setStakeholderCount(n)}
                  className={`w-8 h-8 rounded-md text-xs font-medium transition-all ${
                    stakeholderCount === n ? 'bg-[#0F2040] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`sim-count-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSimulate}
            disabled={simulating}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="simulate-confirm-btn"
          >
            {simulating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
            Simulate
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Bulk Set Status Modal ----
// Lets leadership apply (or clear) the manual workflow-status override
// across many selected estimates in one shot. Mirrors the single-row
// `EstimateStatusSelect` chip's grouping (Workflow / Lifecycle) plus a
// "Clear override" option and an optional note. Shows the affected
// estimate names up front so destructive picks (Won/Lost/Archived)
// have an unambiguous preview before the user confirms.
function BulkSetStatusModal({ selectedIds, workspaces, onClose, onDone }) {
  const [picked, setPicked] = useState(null); // status value or 'CLEAR' sentinel
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectedWs = workspaces.filter(w => selectedIds.has(w.id));
  // Won/Lost/Archived have side-effects (winner mark, compare-set sibling
  // archive, soft-delete). Surface a warning when one is picked so the
  // user doesn't accidentally archive a batch of live deals.
  const DESTRUCTIVE = new Set(['won', 'lost', 'archived']);
  const isDestructive = picked && picked !== 'CLEAR' && DESTRUCTIVE.has(picked);

  const apply = async () => {
    if (!picked) return;
    setSubmitting(true);
    try {
      const body = {
        ids: [...selectedIds],
        status: picked === 'CLEAR' ? null : picked,
        note: (note || '').trim() || null,
      };
      const res = await axios.post(
        `${API_URL}/api/estimates/bulk/workflow-status`,
        body,
        { withCredentials: true },
      );
      const { succeeded, failed } = res.data || {};
      const verb = picked === 'CLEAR' ? 'Cleared override on' : `Set ${statusLabel(picked)} on`;
      if (succeeded > 0) {
        toast.success(`${verb} ${succeeded} estimate${succeeded === 1 ? '' : 's'}${failed > 0 ? ` (${failed} failed)` : ''}`);
      }
      if (succeeded === 0) {
        toast.error('Bulk status update failed for every selected estimate');
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk status update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="bulk-set-status-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-indigo-500" />
            <h2 className="text-sm font-semibold text-[#0F2040]">Set Status</h2>
            <Badge variant="secondary" className="text-[9px]">{selectedIds.size} selected</Badge>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="bulk-set-status-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 overflow-y-auto space-y-4">
          {/* Affected list — preview so the user can verify the batch
              before applying. Capped at 8 names with a "+N more" tail
              to keep the modal compact on very large batches. */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Affected estimates</div>
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2 max-h-32 overflow-y-auto" data-testid="bulk-set-status-affected">
              <ul className="text-[11px] text-slate-700 space-y-0.5">
                {selectedWs.slice(0, 8).map(w => (
                  <li key={w.id} className="truncate" title={w.estimate_name || w.organization_name}>
                    • {w.estimate_name || w.organization_name || 'Untitled'}
                    {w.organization_name && w.estimate_name && (
                      <span className="text-slate-400"> — {w.organization_name}</span>
                    )}
                  </li>
                ))}
                {selectedWs.length > 8 && (
                  <li className="text-slate-400 italic">+ {selectedWs.length - 8} more</li>
                )}
              </ul>
            </div>
          </div>

          {/* Status picker — grouped Workflow / Lifecycle (mirrors
              EstimateStatusSelect) plus a "Clear override" row that
              wipes the manual override and reverts to auto-derived. */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">New status</div>
            <div className="space-y-2">
              {STATUS_GROUPS.map(g => (
                <div key={g.name}>
                  <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">{g.name}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.options.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setPicked(opt.value)}
                        data-testid={`bulk-set-status-option-${opt.value}`}
                        className={`text-[11px] px-2 py-1 rounded-md border transition ${
                          picked === opt.value
                            ? 'border-[#0F2040] ring-1 ring-[#0F2040] bg-white'
                            : 'border-slate-200 hover:border-slate-300 bg-white'
                        }`}
                      >
                        <span className={`inline-block w-2 h-2 rounded-sm mr-1.5 align-middle ${opt.tone}`} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <div>
                <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Override</div>
                <button
                  type="button"
                  onClick={() => setPicked('CLEAR')}
                  data-testid="bulk-set-status-option-clear"
                  className={`text-[11px] px-2 py-1 rounded-md border transition ${
                    picked === 'CLEAR'
                      ? 'border-rose-500 ring-1 ring-rose-500 bg-white text-rose-700'
                      : 'border-slate-200 hover:border-slate-300 bg-white text-rose-700'
                  }`}
                >
                  <X className="w-3 h-3 inline mr-1 align-middle" />
                  Clear override (revert to auto)
                </button>
              </div>
            </div>
          </div>

          {/* Destructive-pick warning. Won → marks winner + archives
              compare-set siblings. Lost / Archived → soft-archive. */}
          {isDestructive && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800 flex items-start gap-2" data-testid="bulk-set-status-destructive-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <div>
                <strong className="font-semibold">Heads-up:</strong>{' '}
                {picked === 'won' && 'Marks every selected estimate as the winner of its compare-set. Sibling estimates in those compare-sets will be auto-archived as losers.'}
                {picked === 'lost' && 'Soft-archives every selected estimate (archived_reason="manual_lost"). They\'ll move to the Archived view.'}
                {picked === 'archived' && 'Soft-archives every selected estimate. They\'ll move to the Archived view.'}
              </div>
            </div>
          )}

          {/* Optional note — stored on every override row for the audit
              trail. Visible in the per-estimate status chip tooltip. */}
          <div>
            <label className="text-[10px] text-slate-500 font-medium mb-1 block">Note (optional)</label>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Why this bulk change?"
              className="text-xs"
              data-testid="bulk-set-status-note"
              maxLength={500}
            />
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50/40">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="bulk-set-status-cancel">Cancel</Button>
          <Button
            size="sm"
            onClick={apply}
            disabled={!picked || submitting}
            className={isDestructive ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#0F2040] hover:bg-[#1A3055]'}
            data-testid="bulk-set-status-apply"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Tag className="w-3.5 h-3.5 mr-1" />}
            {picked === 'CLEAR'
              ? `Clear override on ${selectedIds.size}`
              : picked
                ? `Apply "${statusLabel(picked)}" to ${selectedIds.size}`
                : 'Choose a status'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Main Dashboard ----
export default function WorkspaceDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recentMode = searchParams.get('recent') === '1';
  const [viewerNoticeDismissed, setViewerNoticeDismissed] = useState(
    () => localStorage.getItem('viewer_welcome_dismissed') === '1'
  );
  const dismissViewerNotice = () => {
    localStorage.setItem('viewer_welcome_dismissed', '1');
    setViewerNoticeDismissed(true);
  };

  // ── URL-state hydration (v260.31) ─────────────────────────────────────
  // Every triage filter on /workspaces is now URL-encoded so reps can
  // bookmark or share a view. The URL is the source of truth on mount;
  // a single useEffect below mirrors state changes back into the URL
  // (replaceState so we don't pollute history with every keystroke).
  const urlInit = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return {
        search: sp.get('q') || '',
        status: sp.get('status') || '',
        archived: sp.get('archived') === '1',
        presales: sp.get('presales') === '1',
        assignedToMe: sp.get('mine') === '1',
        salesLead: sp.get('sales_lead') || '',
        csAssignedToMe: sp.get('cs_mine') === '1',
        csLead: sp.get('cs_lead') || '',
        hasDocuments: sp.get('docs') || '',
        locked: sp.get('locked') || '',
        subscription: sp.get('subscription') || '',
        stage: sp.get('stage') || '',
        segments: sp.get('seg') ? sp.get('seg').split(',').filter(Boolean) : [],
        entities: sp.get('ent') ? sp.get('ent').split(',').filter(Boolean) : [],
        regions: sp.get('reg') ? sp.get('reg').split(',').filter(Boolean) : [],
        page: Math.max(1, parseInt(sp.get('page'), 10) || 1),
        view: sp.get('view') || null,
      };
    } catch {
      return { search: '', status: '', archived: false, presales: false, assignedToMe: false, salesLead: '', csAssignedToMe: false, csLead: '', hasDocuments: '', segments: [], entities: [], regions: [], page: 1, view: null };
    }
  })();

  const [workspaces, setWorkspaces] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(urlInit.page);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(urlInit.search);
  const [searchInput, setSearchInput] = useState(urlInit.search);
  const [statusFilter, setStatusFilter] = useState(urlInit.status);
  const [segmentFilter, setSegmentFilter] = useState(urlInit.segments);
  // v378 — Entity / Region segmentation filters + the entity list for options.
  const [entityFilter, setEntityFilter] = useState(urlInit.entities);
  const [regionFilter, setRegionFilter] = useState(urlInit.regions);
  const [wsEntities, setWsEntities] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/settings/seertech-entities`, { withCredentials: true })
      .then(res => setWsEntities(res.data?.entities || []))
      .catch(() => setWsEntities([]));
  }, []);
  const wsRegionOptions = React.useMemo(() => {
    const set = new Set();
    wsEntities.forEach(en => {
      (en.regions || []).forEach(r => set.add(r));
      if (en.region) set.add(en.region);
    });
    return [...set].sort();
  }, [wsEntities]);
  // Portfolio wave slip — summary drives the KPI tile; the full card
  // expands below the tiles when the user selects the tile.
  const [slipSummary, setSlipSummary] = useState(null);
  const [showSlip, setShowSlip] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/planner/waves-slip-rollup`, { withCredentials: true })
      .then(res => setSlipSummary(res.data?.estimates?.length ? res.data.summary : null))
      .catch(() => {});
  }, []);
  // Remember My View — per-user sort + completed-visibility preferences
  const viewPrefsKey = `ws_view_prefs_${user?.id || 'anon'}`;
  const savedPrefs = (() => {
    try { return JSON.parse(localStorage.getItem(viewPrefsKey)) || {}; } catch { return {}; }
  })();
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.some(o => o.id === savedPrefs.sortBy) ? savedPrefs.sortBy : 'name');
  const [sortDir, setSortDir] = useState(savedPrefs.sortDir === 'desc' ? 'desc' : 'asc');
  const [showCompleted, setShowCompleted] = useState(!!savedPrefs.showCompleted);

  useEffect(() => {
    try {
      localStorage.setItem(viewPrefsKey, JSON.stringify({ sortBy, sortDir, showCompleted }));
    } catch { /* private mode */ }
  }, [viewPrefsKey, sortBy, sortDir, showCompleted]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [signoffWs, setSignoffWs] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showSimModal, setShowSimModal] = useState(false);
  const [showSetStatusModal, setShowSetStatusModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [viewMode, setViewMode] = useState(() => urlInit.view || getInitialView());
  // Snapshot the user's session-start preferred view so the URL mirror
  // can tell "user explicitly changed view" apart from "user is on
  // their default view". Without this ref, setViewModePersist() writes
  // localStorage BEFORE the mirror useEffect runs, so the divergence
  // check always reads false and view= never appears in shared links.
  const sessionDefaultViewRef = useRef(urlInit.view || getInitialView());
  const [pinned, setPinned] = useState([]);
  const [pinCap, setPinCap] = useState(5);
  const [pinningId, setPinningId] = useState(null);
  const limit = 12;

  const setViewModePersist = useCallback((mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_KEY, mode);
    } catch (err) {
      // QuotaExceededError or private-mode block — UI state still
      // reflects the toggle; only persistence is lost.
      logger.warn('Workspaces view-mode persistence skipped:', err?.name || err);
    }
  }, []);

  const [showArchived, setShowArchived] = useState(urlInit.archived);
  const [presalesMode, setPresalesMode] = useState(urlInit.presales);
  // Sales Lead pipeline filters: "My assigned deals" (current user) and a
  // specific-rep filter for leadership. salesReps populates the dropdown.
  const [assignedToMe, setAssignedToMe] = useState(urlInit.assignedToMe);
  const [salesLeadFilter, setSalesLeadFilter] = useState(urlInit.salesLead);
  const [salesReps, setSalesReps] = useState([]);
  // CS Lead pipeline filters: "My CS deals" (current user as CS Lead) and a
  // specific-CS-rep filter for CS managers. csReps populates the dropdown.
  const [csAssignedToMe, setCsAssignedToMe] = useState(urlInit.csAssignedToMe);
  const [csLeadFilter, setCsLeadFilter] = useState(urlInit.csLead);
  const [csReps, setCsReps] = useState([]);
  // Supporting-material filter: '' any · 'yes' has docs · 'no' missing docs.
  const [hasDocuments, setHasDocuments] = useState(urlInit.hasDocuments);
  const [lockedFilter, setLockedFilter] = useState(urlInit.locked || '');
  const [subscriptionFilter, setSubscriptionFilter] = useState(urlInit.subscription || '');
  const [stageFilter, setStageFilter] = useState(urlInit.stage || '');

  useEffect(() => {
    if (user?.role === 'user') return; // viewers can't access lead lists (403)
    axios.get(`${API_URL}/api/estimates/assignable-sales-leads`, { withCredentials: true })
      .then(res => setSalesReps(res.data?.sales_leads || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/estimates/assignable-cs-leads`, { withCredentials: true })
      .then(res => setCsReps(res.data?.cs_leads || []))
      .catch(() => {});
  }, [user?.role]);

  const fetchWorkspaces = useCallback(async (p = page, q = search, s = statusFilter) => {
    setLoading(true);
    try {
      const params = { page: p, limit, search: q, status: s, archived: showArchived, presales: presalesMode, sort_by: sortBy, sort_dir: sortDir, include_completed: showCompleted };
      if (assignedToMe) params.assigned_to_me = true;
      else if (csAssignedToMe) params.cs_assigned_to_me = true;
      else {
        if (salesLeadFilter) params.sales_lead = salesLeadFilter;
        if (csLeadFilter) params.cs_lead = csLeadFilter;
      }
      if (hasDocuments) params.has_documents = hasDocuments;
      if (lockedFilter) params.locked = lockedFilter;
      if (subscriptionFilter) params.subscription = subscriptionFilter;
      if (stageFilter) params.stage = stageFilter;
      if (segmentFilter.length) params.segments = segmentFilter.join(',');
      if (entityFilter.length) params.entity = entityFilter.join(',');
      if (regionFilter.length) params.region = regionFilter.join(',');
      const res = await axios.get(`${API_URL}/api/workspaces/summary`, { params, withCredentials: true });
      setWorkspaces(res.data.workspaces || []);
      setTotal(res.data.total || 0);
      setKpis(res.data.kpis || {});
    } catch (err) {
      logger.error('Failed to fetch workspaces:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, showArchived, presalesMode, assignedToMe, salesLeadFilter, csAssignedToMe, csLeadFilter, hasDocuments, lockedFilter, subscriptionFilter, stageFilter, segmentFilter, entityFilter, regionFilter, sortBy, sortDir, showCompleted]);

  useEffect(() => {
    fetchWorkspaces(page, search, statusFilter);
  }, [page, search, statusFilter, showArchived, presalesMode, assignedToMe, salesLeadFilter, csAssignedToMe, csLeadFilter, hasDocuments, lockedFilter, subscriptionFilter, stageFilter, fetchWorkspaces]);

  // Mirror the full filter+view+page state into the URL so the page is
  // bookmarkable end-to-end ("here are all my deals in review" → DM the
  // link). replaceState (not pushState) so back/forward isn't littered
  // with every keystroke; the initial URL → state hydration above is
  // the only place the URL is read.
  useEffect(() => {
    try {
      const sp = new URLSearchParams();
      if (search) sp.set('q', search);
      if (statusFilter) sp.set('status', statusFilter);
      if (showArchived) sp.set('archived', '1');
      if (presalesMode) sp.set('presales', '1');
      if (assignedToMe) sp.set('mine', '1');
      else if (csAssignedToMe) sp.set('cs_mine', '1');
      else {
        if (salesLeadFilter) sp.set('sales_lead', salesLeadFilter);
        if (csLeadFilter) sp.set('cs_lead', csLeadFilter);
      }
      if (hasDocuments) sp.set('docs', hasDocuments);
      if (lockedFilter) sp.set('locked', lockedFilter);
      if (subscriptionFilter) sp.set('subscription', subscriptionFilter);
      if (stageFilter) sp.set('stage', stageFilter);
      if (segmentFilter.length) sp.set('seg', segmentFilter.join(','));
      if (entityFilter.length) sp.set('ent', entityFilter.join(','));
      if (regionFilter.length) sp.set('reg', regionFilter.join(','));
      if (page > 1) sp.set('page', String(page));
      // Only emit view= when it diverges from the user's session-start
      // default (snapshot in sessionDefaultViewRef). Without this, the
      // mirror would never emit view= because setViewModePersist writes
      // localStorage synchronously before this effect runs, leaving
      // viewMode === getInitialView() always.
      if (viewMode && viewMode !== sessionDefaultViewRef.current) sp.set('view', viewMode);
      // Preserve the `recent=1` flag from the recent-views entry point.
      if (recentMode) sp.set('recent', '1');
      const next = `${window.location.pathname}${sp.toString() ? '?' + sp.toString() : ''}`;
      window.history.replaceState(null, '', next);
    } catch (err) {
      // URL update failed — non-critical (sweepable on next render).
      console.warn('WorkspaceDashboard: URL sync failed', err);
    }
  }, [search, statusFilter, showArchived, presalesMode, assignedToMe, salesLeadFilter, csAssignedToMe, csLeadFilter, hasDocuments, lockedFilter, subscriptionFilter, stageFilter, segmentFilter, entityFilter, regionFilter, page, viewMode, recentMode]);

  // Archive / restore handlers — fire-and-forget then refresh the list.
  // Errors surface via toast.
  const archiveWorkspace = useCallback(async (estimateId) => {
    try {
      await axios.post(`${API_URL}/api/estimates/${estimateId}/archive`, {}, { withCredentials: true });
      toast.success('Estimate archived');
      fetchWorkspaces(page, search, statusFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to archive');
    }
  }, [fetchWorkspaces, page, search, statusFilter]);

  const restoreWorkspace = useCallback(async (estimateId) => {
    try {
      await axios.post(`${API_URL}/api/estimates/${estimateId}/unarchive`, {}, { withCredentials: true });
      toast.success('Estimate restored');
      fetchWorkspaces(page, search, statusFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore');
    }
  }, [fetchWorkspaces, page, search, statusFilter]);

  const deleteWorkspace = useCallback(async (estimateId) => {
    if (!window.confirm('Permanently delete this estimate?\n\nThis is irreversible — the estimate, all its versions, and saved planner overrides will be removed. Use Archive if you only want to hide it from active views.')) return;
    try {
      await axios.delete(`${API_URL}/api/estimates/${estimateId}`, { withCredentials: true });
      toast.success('Estimate deleted');
      fetchWorkspaces(page, search, statusFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  }, [fetchWorkspaces, page, search, statusFilter]);

  // Super-admin bulk delete — wired to the "Delete Selected" button in
  // the bulk-action toolbar. Single confirm covers the whole batch
  // (vs N row-level confirms) so cleanup of legacy / test estimates
  // doesn't take forever. Server-side already enforces super_admin
  // role — this UI guard is just to hide the button for non-admins.
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const bulkDeleteSelected = useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} estimate${ids.length > 1 ? 's' : ''}?\n\nThis is irreversible — every selected estimate plus its version history and saved planner overrides will be removed. Use Archive if you only want to hide them from active views.`)) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/estimates/bulk-delete`,
        { ids },
        { withCredentials: true },
      );
      const { estimates_deleted, versions_deleted, plans_deleted } = res.data || {};
      toast.success(
        `Deleted ${estimates_deleted} estimate${estimates_deleted === 1 ? '' : 's'} · ${versions_deleted} version row${versions_deleted === 1 ? '' : 's'} · ${plans_deleted} saved plan${plans_deleted === 1 ? '' : 's'}`,
      );
      setSelectedIds(new Set());
      fetchWorkspaces(page, search, statusFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  }, [selectedIds, fetchWorkspaces, page, search, statusFilter]);

  // Auto-archive sweep + undo banner. On first mount of the dashboard
  // per session, fire the backend sweep that auto-archives any of the
  // caller's estimates that haven't been touched in
  // AUTO_ARCHIVE_STALE_DAYS days (90 by default). If anything was
  // archived, surface a yellow undo banner so the user can either
  // ignore it (already cleaned-up) or click "Undo all" to bring
  // everything back. Session-scoped guard via sessionStorage so the
  // banner doesn't reappear every time the user navigates back here.
  const [autoArchived, setAutoArchived] = useState(null);
  const wsActiveFilterCount = [assignedToMe, csAssignedToMe, salesLeadFilter, csLeadFilter, hasDocuments, lockedFilter, subscriptionFilter, stageFilter, presalesMode, showArchived].filter(Boolean).length + segmentFilter.length + entityFilter.length + regionFilter.length;
  useEffect(() => {
    if (sessionStorage.getItem('autoArchiveSweepDone') === '1') return;
    sessionStorage.setItem('autoArchiveSweepDone', '1');
    (async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/estimates/auto-archive-stale`,
          {},
          { withCredentials: true },
        );
        if ((res.data?.archived_count || 0) > 0) {
          setAutoArchived(res.data);
          // Refresh the active list so the swept rows disappear.
          fetchWorkspaces(page, search, statusFilter);
        }
      } catch (err) {
        // Silent — the sweep is a quality-of-life feature, not critical
        // path. If it fails the user just keeps the old behaviour.
        console.warn('WorkspaceDashboard: auto-archive sweep failed', err);
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const undoAutoArchive = useCallback(async () => {
    if (!autoArchived?.archived_ids?.length) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/estimates/bulk-unarchive`,
        { ids: autoArchived.archived_ids },
        { withCredentials: true },
      );
      toast.success(`Restored ${res.data?.restored || 0} estimates`);
      setAutoArchived(null);
      fetchWorkspaces(page, search, statusFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore');
    }
  }, [autoArchived, fetchWorkspaces, page, search, statusFilter]);

  const dismissAutoArchive = useCallback(() => setAutoArchived(null), []);

  // Bulk Restore — counterpart to the single-row ArchiveRestore icon.
  // Only mounted in the Archived view. No confirmation dialog (restore
  // is reversible — they can just archive again) so the action feels
  // light and the toolbar stays snappy.
  const [bulkRestoring, setBulkRestoring] = useState(false);
  const bulkRestoreSelected = useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkRestoring(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/estimates/bulk-unarchive`,
        { ids },
        { withCredentials: true },
      );
      const { restored } = res.data || {};
      toast.success(`Restored ${restored} estimate${restored === 1 ? '' : 's'} to active`);
      setSelectedIds(new Set());
      fetchWorkspaces(page, search, statusFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk restore failed');
    } finally {
      setBulkRestoring(false);
    }
  }, [selectedIds, fetchWorkspaces, page, search, statusFilter]);

  // Bulk Archive — soft-archives the selected estimates by looping the
  // existing per-row POST /api/estimates/{id}/archive endpoint. Used in
  // the active view (mirror of bulkRestoreSelected on the archived view).
  // No dedicated backend bulk endpoint exists — looping single calls is
  // acceptable at the dashboard's typical batch size (≤20 rows on screen).
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const bulkArchiveSelected = useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Archive ${ids.length} estimate${ids.length === 1 ? '' : 's'}? They'll move to the Archived view and can be restored from there.`)) return;
    setBulkArchiving(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await axios.post(`${API_URL}/api/estimates/${id}/archive`, {}, { withCredentials: true });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    if (ok > 0) toast.success(`Archived ${ok} estimate${ok === 1 ? '' : 's'}${fail > 0 ? ` (${fail} failed)` : ''}`);
    if (ok === 0) toast.error('Bulk archive failed for every selected estimate');
    setBulkArchiving(false);
    setSelectedIds(new Set());
    fetchWorkspaces(page, search, statusFilter);
  }, [selectedIds, fetchWorkspaces, page, search, statusFilter]);

  // Bulk PDF Export — fans out per-estimate Scope Pack PDF generation.
  // Each call fetches the full estimate doc + the global component
  // catalogue (cached after the first hit), then triggers a client-side
  // jsPDF download via exportScopePack. Sequential to keep the browser
  // download tray from getting blocked by the popup blocker.
  const [bulkExporting, setBulkExporting] = useState(false);
  const bulkExportPdf = useCallback(async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkExporting(true);
    let componentsCache = null;
    let ok = 0, fail = 0;
    const progressToastId = toast.loading(`Exporting 0/${ids.length} estimates…`);
    for (let i = 0; i < ids.length; i += 1) {
      const id = ids[i];
      try {
        const [estRes, compsRes] = await Promise.all([
          axios.get(`${API_URL}/api/estimates/${id}`, { withCredentials: true }),
          componentsCache
            ? Promise.resolve({ data: { components: componentsCache } })
            : axios.get(`${API_URL}/api/components`, { withCredentials: true }),
        ]);
        componentsCache = componentsCache || (compsRes.data?.components || []);
        exportScopePack(estRes.data, componentsCache);
        ok += 1;
      } catch (err) {
        fail += 1;
        logger.warn(`bulk-export-pdf: estimate ${id} failed`, err?.message);
      }
      toast.loading(`Exporting ${i + 1}/${ids.length} estimates…`, { id: progressToastId });
      // Brief stagger so the browser download UI keeps up across rows.
      await new Promise(r => setTimeout(r, 250));
    }
    toast.dismiss(progressToastId);
    if (ok > 0) toast.success(`Exported ${ok} PDF${ok === 1 ? '' : 's'}${fail > 0 ? ` (${fail} failed)` : ''}`);
    if (ok === 0) toast.error('Bulk PDF export failed for every selected estimate');
    setBulkExporting(false);
  }, [selectedIds]);

  // ── Pinned rail (v185) ────────────────────────────────────────────────
  // Lets the user star up to 5 active estimates so they always sit at
  // the top of Workspaces (roams across devices via user preferences).
  const fetchPinned = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/me/pinned-estimates`, { withCredentials: true });
      setPinned(res.data?.pinned || []);
      setPinCap(res.data?.cap || 5);
    } catch (err) {
      logger.error('Failed to fetch pinned estimates:', err);
    }
  }, []);

  useEffect(() => { fetchPinned(); }, [fetchPinned]);

  const pinnedIdSet = React.useMemo(() => new Set(pinned.map(p => p.id)), [pinned]);

  const togglePin = useCallback(async (estimateId, evt) => {
    if (evt) { evt.preventDefault(); evt.stopPropagation(); }
    if (pinningId) return;
    setPinningId(estimateId);
    try {
      const res = await axios.post(
        `${API_URL}/api/users/me/pinned-estimates/${estimateId}`,
        {},
        { withCredentials: true },
      );
      setPinned(res.data?.pinned || []);
      setPinCap(res.data?.cap || 5);
      toast.success(res.data?.action === 'pinned' ? 'Pinned' : 'Unpinned', { duration: 1500 });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Pin failed';
      toast.error(msg);
    } finally {
      setPinningId(null);
    }
  }, [pinningId]);
  // ──────────────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / limit);

  const handleSearch = () => { setPage(1); setSearch(searchInput); };
  const handleFilterChange = (s) => { setStatusFilter(s); setPage(1); };
  // Multi-select status pills (comma-separated in state/URL/API, v369)
  const statusParts = statusFilter ? statusFilter.split(',').filter(Boolean) : [];
  const toggleStatusPill = (id) => {
    setStatusFilter(prev => {
      const parts = prev ? prev.split(',').filter(Boolean) : [];
      return (parts.includes(id) ? parts.filter(x => x !== id) : [...parts, id]).join(',');
    });
    setPage(1);
  };
  const toggleSegmentPill = (id) => {
    setSegmentFilter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setPage(1);
  };
  const toggleListFilter = (type, id) => {
    if (type === 'segment') toggleSegmentPill(id);
    else toggleStatusPill(id);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === workspaces.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(workspaces.map(w => w.id)));
    }
  };

  // ── Register ⌘K palette commands for the Workspace dashboard ──
  // Surfaces the loaded workspaces as quick "Open workspace" jumps,
  // plus filter shortcuts for the status tabs.
  usePaletteCommands(
    'workspace-dashboard-actions',
    () => {
      const items = [];
      // Status filter shortcuts
      FILTER_TABS.forEach((t) => {
        if (t.id === statusFilter) return;
        items.push({
          id: `ws-filter-${t.id || 'all'}`,
          value: `Filter workspaces by ${t.label}`,
          icon: Filter,
          label: `Filter: ${t.label}`,
          onSelect: () => { setStatusFilter(t.id); setPage(1); },
          closeOnSelect: false,
        });
      });
      // Top-8 visible workspaces as "Open workspace" jumps
      workspaces.slice(0, 8).forEach((w) => {
        const label = w.estimate_name || w.organization_name || 'Untitled';
        items.push({
          id: `ws-open-${w.id}`,
          value: `Open workspace ${w.estimate_name || ''} ${w.organization_name || ''} ${w.status || ''} ${w.id}`,
          icon: FolderOpen,
          label: `Open: ${label}`,
          onSelect: () => navigate(`/workspace/${w.id}/overview`),
        });
      });
      return items.length > 0 ? { heading: 'Workspaces', priority: 5, items } : null;
    },
    { deps: [workspaces, statusFilter, navigate] }
  );

  const handleModalDone = () => {
    setShowSendModal(false);
    setShowSimModal(false);
    setShowSetStatusModal(false);
    setSelectedIds(new Set());
    fetchWorkspaces(page, search, statusFilter);
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await axios.post(`${API_URL}/api/workspaces/simulate/reset`, {}, { withCredentials: true });
      toast.success(`Reset ${res.data.deleted} simulated entries`);
      setSelectedIds(new Set());
      fetchWorkspaces(page, search, statusFilter);
    } catch (err) {
      toast.error('Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="workspace-dashboard">
      {user?.role === 'user' && !viewerNoticeDismissed && (
        <div className="bg-[#0F2040] text-white" data-testid="viewer-welcome-panel">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-start gap-3">
            <Eye className="w-4 h-4 text-[#4ABFCF] mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Welcome — you have view-only access</p>
              <p className="text-xs text-slate-300 mt-0.5">
                You can browse every estimate shared with your organisation and open its
                Planner timeline and Full Report. Editing, comparing
                and configuration are handled by the Seertech team. Need broader access or
                can't find an estimate? Contact your Seertech administrator.
              </p>
            </div>
            <button onClick={dismissViewerNotice} className="text-slate-400 hover:text-white transition-colors shrink-0"
              title="Dismiss" data-testid="viewer-welcome-dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-[#0F2040] font-['Outfit'] flex items-center gap-2" data-testid="workspaces-heading">
                <span>{showArchived ? 'Archived Estimates' : (presalesMode ? 'Pre-Sales — Requirements Captured, No SOW Yet' : (recentMode ? 'Recently Viewed' : 'Workspaces'))}</span>
                {showArchived && (
                  <span aria-hidden="true" className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 uppercase tracking-wide">
                    Archive
                  </span>
                )}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {showArchived
                  ? 'Soft-deleted estimates. Restore to active or permanently delete.'
                  : (recentMode
                    ? 'Pick up where you left off — most recent estimates first.'
                    : 'Create a new estimate or jump back into one in flight.')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* View toggle (list ↔ grid) — list is the default at scale
                  because 100+ identical-looking cards are unscannable. */}
              <div className="hidden sm:flex items-center bg-slate-100 rounded-md p-0.5" data-testid="workspaces-view-toggle">
                <button
                  onClick={() => setViewModePersist('list')}
                  className={`p-1 rounded text-slate-500 transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-[#0F2040]' : 'hover:text-[#0F2040]'}`}
                  title="List view"
                  data-testid="workspaces-view-list"
                >
                  <List className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setViewModePersist('grid')}
                  className={`p-1 rounded text-slate-500 transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-[#0F2040]' : 'hover:text-[#0F2040]'}`}
                  title="Grid view"
                  data-testid="workspaces-view-grid"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                </button>
              </div>
              {/* Consolidated filters — one popover instead of a pill row */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-slate-600" data-testid="workspaces-filters-btn">
                    <Filter className="w-3.5 h-3.5 mr-1" />
                    Filters
                    {wsActiveFilterCount > 0 && (
                      <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#4ABFCF] text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="workspaces-filters-count">{wsActiveFilterCount}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-3">
                  <div className="flex flex-col items-stretch gap-2 [&>button]:justify-start [&_button]:w-full" data-testid="workspaces-filters-panel">
                    <div>
                      <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Estimate type</div>
                      <div className="flex flex-wrap items-center gap-1">
                        {WS_SEGMENT_OPTIONS.map((s) => (
                          <button key={s.id} type="button"
                            onClick={() => { setSegmentFilter((prev) => prev.includes(s.id) ? prev.filter(x => x !== s.id) : [...prev, s.id]); setPage(1); }}
                            data-testid={`workspaces-segment-${s.id}`}
                            className={`!w-auto text-[10px] px-2 py-0.5 rounded-full border ${
                              segmentFilter.includes(s.id)
                                ? 'bg-[#0F2040] text-white border-[#0F2040]'
                                : 'border-slate-300 text-slate-600 hover:border-slate-400'
                            }`}>{s.label}</button>
                        ))}
                      </div>
                    </div>
                    {wsEntities.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Entity</div>
                        <div className="flex flex-wrap items-center gap-1">
                          {wsEntities.map((en) => (
                            <button key={en.id} type="button"
                              onClick={() => { setEntityFilter((prev) => prev.includes(en.id) ? prev.filter(x => x !== en.id) : [...prev, en.id]); setPage(1); }}
                              data-testid={`workspaces-entity-${en.id}`}
                              className={`!w-auto text-[10px] px-2 py-0.5 rounded-full border ${
                                entityFilter.includes(en.id)
                                  ? 'bg-[#0F2040] text-white border-[#0F2040]'
                                  : 'border-slate-300 text-slate-600 hover:border-slate-400'
                              }`}>{en.code || en.id}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {wsRegionOptions.length > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">Region</div>
                        <div className="flex flex-wrap items-center gap-1">
                          {wsRegionOptions.map((r) => (
                            <button key={r} type="button"
                              onClick={() => { setRegionFilter((prev) => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]); setPage(1); }}
                              data-testid={`workspaces-region-${r.toLowerCase()}`}
                              className={`!w-auto text-[10px] px-2 py-0.5 rounded-full border ${
                                regionFilter.includes(r)
                                  ? 'bg-[#0F2040] text-white border-[#0F2040]'
                                  : 'border-slate-300 text-slate-600 hover:border-slate-400'
                              }`}>{r}</button>
                          ))}
                        </div>
                      </div>
                    )}
                    {user?.role !== 'user' && (<>
              <Button
                variant={assignedToMe ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setAssignedToMe((v) => !v); if (!assignedToMe) { setSalesLeadFilter(''); setCsLeadFilter(''); setCsAssignedToMe(false); } setPage(1); }}
                className={assignedToMe ? 'bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white' : 'text-[#0F2040] border-[#4ABFCF]/40 hover:bg-[#4ABFCF]/10'}
                data-testid="workspaces-assigned-to-me-toggle"
                title={assignedToMe ? 'Show all estimates' : 'Show only estimates where I am the Sales or CS Lead'}
              >
                <UserCircle2 className="w-3.5 h-3.5 mr-1" />
                {assignedToMe ? 'Viewing My Deals' : 'My Assigned Deals'}
              </Button>
              {!assignedToMe && !csAssignedToMe && salesReps.length > 0 && (
                <Select
                  value={salesLeadFilter || '__all__'}
                  onValueChange={(v) => { setSalesLeadFilter(v === '__all__' ? '' : v); setPage(1); }}
                >
                  <SelectTrigger className="h-9 w-44 text-xs" data-testid="workspaces-sales-lead-filter">
                    <SelectValue placeholder="All Sales Leads" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Sales Leads</SelectItem>
                    {salesReps.map((r) => (
                      <SelectItem key={r.user_id} value={r.user_id} data-testid={`ws-sl-filter-${r.user_id}`}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                variant={csAssignedToMe ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setCsAssignedToMe((v) => !v); if (!csAssignedToMe) { setCsLeadFilter(''); setSalesLeadFilter(''); setAssignedToMe(false); } setPage(1); }}
                className={csAssignedToMe ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'}
                data-testid="workspaces-cs-assigned-to-me-toggle"
                title={csAssignedToMe ? 'Show all estimates' : 'Show only estimates where I am the CS Lead'}
              >
                <Headset className="w-3.5 h-3.5 mr-1" />
                {csAssignedToMe ? 'Viewing My CS Deals' : 'My CS Deals'}
              </Button>
              {!assignedToMe && !csAssignedToMe && csReps.length > 0 && (
                <Select
                  value={csLeadFilter || '__all__'}
                  onValueChange={(v) => { setCsLeadFilter(v === '__all__' ? '' : v); setPage(1); }}
                >
                  <SelectTrigger className="h-9 w-44 text-xs" data-testid="workspaces-cs-lead-filter">
                    <SelectValue placeholder="All CS Leads" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All CS Leads</SelectItem>
                    {csReps.map((r) => (
                      <SelectItem key={r.user_id} value={r.user_id} data-testid={`ws-cs-filter-${r.user_id}`}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select
                value={hasDocuments || '__any__'}
                onValueChange={(v) => { setHasDocuments(v === '__any__' ? '' : v); setPage(1); }}
              >
                <SelectTrigger className="h-9 w-40 text-xs" data-testid="workspaces-has-documents-filter">
                  <Paperclip className="w-3 h-3 mr-1 text-slate-400" />
                  <SelectValue placeholder="Documents" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__" data-testid="ws-docs-filter-any">Any documents</SelectItem>
                  <SelectItem value="yes" data-testid="ws-docs-filter-yes">Has documents</SelectItem>
                  <SelectItem value="no" data-testid="ws-docs-filter-no">Missing documents</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={lockedFilter || '__any__'}
                onValueChange={(v) => { setLockedFilter(v === '__any__' ? '' : v); setPage(1); }}
              >
                <SelectTrigger className="h-9 w-40 text-xs" data-testid="workspaces-locked-filter">
                  <Lock className="w-3 h-3 mr-1 text-slate-400" />
                  <SelectValue placeholder="Effort & cost lock" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__" data-testid="ws-locked-filter-any">Locked or not</SelectItem>
                  <SelectItem value="true" data-testid="ws-locked-filter-true">Effort & cost locked</SelectItem>
                  <SelectItem value="false" data-testid="ws-locked-filter-false">Not locked</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={subscriptionFilter || '__any__'}
                onValueChange={(v) => { setSubscriptionFilter(v === '__any__' ? '' : v); setPage(1); }}
              >
                <SelectTrigger className="h-9 w-44 text-xs" data-testid="workspaces-subscription-filter">
                  <Package className="w-3 h-3 mr-1 text-slate-400" />
                  <SelectValue placeholder="Product subscription" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__" data-testid="ws-subscription-filter-any">With or without subscription</SelectItem>
                  <SelectItem value="yes" data-testid="ws-subscription-filter-yes">Has product subscription</SelectItem>
                  <SelectItem value="no" data-testid="ws-subscription-filter-no">No product subscription</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={stageFilter || '__any__'}
                onValueChange={(v) => { setStageFilter(v === '__any__' ? '' : v); setPage(1); }}
              >
                <SelectTrigger className="h-9 w-44 text-xs" data-testid="workspaces-stage-filter">
                  <Flag className="w-3 h-3 mr-1 text-slate-400" />
                  <SelectValue placeholder="Deal stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__" data-testid="ws-stage-filter-any">Any deal stage</SelectItem>
                  {DEAL_STAGES.map((st) => <SelectItem key={st.key} value={st.key} data-testid={`ws-stage-filter-${st.key}`}>{st.pct}% · {st.label}</SelectItem>)}
                  <SelectItem value="none" data-testid="ws-stage-filter-none">No stage set</SelectItem>
                </SelectContent>
              </Select>
              </>)}
              <Button
                variant={presalesMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setPresalesMode((v) => !v); if (!presalesMode) setShowArchived(false); setPage(1); }}
                className={presalesMode ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : 'text-cyan-700 border-cyan-200 hover:bg-cyan-50'}
                data-testid="workspaces-presales-toggle"
                title={presalesMode ? 'Switch back to all workspaces' : 'Show only estimates with a Requirements Doc but no SOW yet'}
              >
                <FileText className="w-3.5 h-3.5 mr-1" />
                {presalesMode ? 'Viewing Pre-Sales' : 'Pre-Sales'}
                {!presalesMode && (kpis?.presales || 0) > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-bold" data-testid="workspaces-presales-count">
                    {kpis.presales}
                  </span>
                )}
              </Button>
              <Button
                variant={showArchived ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setShowArchived((v) => !v); if (!showArchived) setPresalesMode(false); setPage(1); }}
                className={showArchived ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'text-amber-700 border-amber-200 hover:bg-amber-50'}
                data-testid="workspaces-archive-toggle"
                title={showArchived ? 'Switch back to active estimates' : 'Show archived (soft-deleted) estimates'}
              >
                <Archive className="w-3.5 h-3.5 mr-1" />
                {showArchived ? 'Viewing Archived' : `Archived${kpis?.archived ? ` (${kpis.archived})` : ''}`}
              </Button>
                  </div>
                </PopoverContent>
              </Popover>
              {/* Overflow menu — Reset Sims / Activity / Quick */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="text-slate-500 px-2" data-testid="workspaces-more-btn" title="More actions">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-2">
                  <div className="flex flex-col items-stretch gap-1.5 [&_button]:w-full [&>button]:justify-start" data-testid="workspaces-more-panel">
                    {user?.role !== 'user' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/quick-estimate')}
                className="text-[#4ABFCF] border-[#4ABFCF]/30 hover:bg-[#4ABFCF]/5"
                data-testid="quick-estimate-nav-btn"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                Quick
              </Button>
              )}
                    {user?.role !== 'user' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={resetting}
                className="text-slate-500"
                data-testid="reset-simulations-btn"
                title="Reset all simulated data"
              >
                <RotateCcw className={`w-3.5 h-3.5 mr-1 ${resetting ? 'animate-spin' : ''}`} />
                Reset Sims
              </Button>
              )}
                    <StatusOverrideAuditFeed
                onChange={() => fetchWorkspaces(page, search, statusFilter)}
              />
                  </div>
                </PopoverContent>
              </Popover>
              {/* Audit feed for recent workflow-status overrides. Lets
                  leadership catch bulk mis-clicks fast and revert them
                  in one click without leaving the dashboard. */}
              
              <Dialog open={!!signoffWs} onOpenChange={(o) => { if (!o) setSignoffWs(null); }}>
                <DialogContent className="max-w-lg" data-testid="discount-signoff-dialog">
                  <DialogHeader>
                    <DialogTitle className="text-[#0F2040] text-base">
                      Discount sign-off timeline — {signoffWs?.estimate_name}
                    </DialogTitle>
                  </DialogHeader>
                  {signoffWs && (() => {
                    const pct = signoffWs.rate_card_blanket_discount_pct;
                    const events = [];
                    if (signoffWs.discount_alerted_at) {
                      events.push({ at: signoffWs.discount_alerted_at, action: 'flagged',
                        text: `Flagged for leadership sign-off (${signoffWs.discount_alerted_pct ?? pct}% rate-card discount)` });
                    }
                    (signoffWs.discount_approval_log || []).forEach(l => events.push({
                      at: l.at, action: l.action,
                      text: `${l.action === 'approved' ? 'Approved' : 'Rejected'} by ${l.by} (${l.pct}%)${l.note ? ` — "${l.note}"` : ''}`,
                    }));
                    events.sort((a, b) => (a.at || '').localeCompare(b.at || ''));
                    const appr = signoffWs.discount_approved;
                    const rej = signoffWs.discount_rejected;
                    const state = appr && appr.pct === pct ? 'Approved' : (rej && rej.pct === pct ? 'Rejected' : 'Awaiting sign-off');
                    return (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500">
                          Current discount: <span className="font-semibold text-rose-600">{pct}%</span>
                          {' · '}status:{' '}
                          <span className={`font-semibold ${state === 'Approved' ? 'text-emerald-600' : state === 'Rejected' ? 'text-rose-600' : 'text-amber-600'}`}
                            data-testid="signoff-current-state">{state}</span>
                        </p>
                        {events.length === 0 ? (
                          <p className="text-xs text-slate-400" data-testid="signoff-empty">No sign-off activity recorded yet.</p>
                        ) : (
                          <div className="space-y-0">
                            {events.map((ev, i) => (
                              <div key={i} className="flex gap-2.5 text-xs" data-testid={`signoff-event-${i}`}>
                                <div className="flex flex-col items-center">
                                  <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${ev.action === 'approved' ? 'bg-emerald-500' : ev.action === 'rejected' ? 'bg-rose-500' : 'bg-amber-400'}`} />
                                  {i < events.length - 1 && <span className="w-px flex-1 bg-slate-200" />}
                                </div>
                                <div className="pb-3">
                                  <p className="text-slate-700">{ev.text}</p>
                                  <p className="text-[10px] text-slate-400">{(ev.at || '').slice(0, 16).replace('T', ' ')} UTC</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </DialogContent>
              </Dialog>
              
              {user?.role !== 'user' && (
              <Button
                onClick={() => navigate('/estimator/new')}
                className="bg-[#4ABFCF] hover:bg-[#3AAAB9]"
                size="sm"
                data-testid="new-estimate-btn"
              >
                + New Estimate
              </Button>
              )}
            </div>
          </div>

          {/* Auto-archive undo banner — shows once per session if the
              backend swept stale estimates on this dashboard load. Yellow
              treatment to read as "FYI, here's what we did" rather than
              an error. "Undo all" calls bulk-unarchive on the just-
              archived ids; the banner self-dismisses on success. */}
          {autoArchived && autoArchived.archived_count > 0 && (
            <div
              className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
              data-testid="auto-archive-banner"
            >
              <Archive className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-900">
                  Auto-archived {autoArchived.archived_count} estimate{autoArchived.archived_count === 1 ? '' : 's'} you hadn't touched in {autoArchived.threshold_days} days
                </p>
                <p className="text-[11px] text-amber-700 mt-0.5 line-clamp-2">
                  {(autoArchived.archived_names || []).slice(0, 5).join(' · ')}
                  {autoArchived.archived_count > 5 ? ` · +${autoArchived.archived_count - 5} more` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100 h-7"
                  onClick={undoAutoArchive}
                  data-testid="auto-archive-undo-btn"
                >
                  <ArchiveRestore className="w-3.5 h-3.5 mr-1" />
                  Undo all
                </Button>
                <button
                  type="button"
                  onClick={dismissAutoArchive}
                  className="p-1 text-amber-600 hover:text-amber-800 transition-colors"
                  title="Dismiss"
                  data-testid="auto-archive-dismiss-btn"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Pinned Rail (v185) — floats up to 5 starred estimates above
            the chronological list. Hidden when nothing is pinned. */}
        {pinned.length > 0 && (
          <div data-testid="pinned-rail" className="bg-gradient-to-br from-amber-50/60 to-white border border-amber-200/70 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Pin className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Pinned</span>
              <span className="text-[10px] text-slate-400">{pinned.length} / {pinCap}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
              {pinned.map((p) => {
                const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.draft;
                const activity = p.activity_since_last_visit || {};
                return (
                  <div
                    key={p.id}
                    className="group relative bg-white border border-amber-200/80 rounded-md p-2 hover:border-amber-400 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => navigate(getResumePath(p))}
                    data-testid={`pinned-card-${p.id}`}
                    data-has-activity={activity.has_activity ? 'true' : 'false'}
                  >
                    <button
                      type="button"
                      onClick={(e) => togglePin(p.id, e)}
                      disabled={pinningId === p.id}
                      className="absolute top-1 right-1 text-amber-500 hover:text-amber-700 opacity-70 hover:opacity-100 transition-opacity p-0.5"
                      data-testid={`pinned-unpin-${p.id}`}
                      title="Unpin"
                    >
                      <Star className="w-3.5 h-3.5 fill-current" />
                    </button>
                    <div className="text-xs font-semibold text-[#0F2040] truncate pr-5" title={p.estimate_name}>
                      {dedupeName(p.estimate_name)}
                    </div>
                    <div className="text-[10px] text-slate-500 truncate mt-0.5">
                      {p.organization_name || '—'}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge className={`text-[9px] py-0 px-1 ${cfg.color} border`}>{cfg.label}</Badge>
                      {p.implementation_type && (
                        <span className="text-[10px] text-slate-400 truncate">{String(p.implementation_type).replace(/_/g, ' ')}</span>
                      )}
                    </div>
                    {/* Activity-since-last-visit badge — passive notification
                        surface so PMs can see what changed without opening. */}
                    {activity.has_activity && (
                      <div
                        className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100/80 border border-amber-300/60 rounded px-1.5 py-0.5"
                        data-testid={`pinned-activity-${p.id}`}
                        title={`Since you last opened this — ${activity.summary}`}
                      >
                        <Activity className="w-2.5 h-2.5" />
                        <span className="truncate">{activity.summary}</span>
                      </div>
                    )}
                    {!activity.has_activity && activity.last_visited_at && (
                      <div className="mt-1.5 text-[9px] text-slate-400 italic" data-testid={`pinned-no-activity-${p.id}`}>
                        Up to date
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className={`grid grid-cols-2 ${slipSummary ? 'sm:grid-cols-4 lg:grid-cols-8' : 'sm:grid-cols-4 lg:grid-cols-7'} gap-3`} data-testid="workspace-kpis">
          <Card className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${statusParts.length === 0 ? 'ring-2 ring-[#4ABFCF]' : ''}`} onClick={() => handleFilterChange('')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-[#0F2040] font-['Outfit']" data-testid="kpi-total">{kpis.total || 0}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Total Active</p>
              {kpis.segments && (
                <div className="flex items-center justify-center gap-1 mt-1 flex-wrap" data-testid="kpi-type-mix">
                  {WS_SEGMENT_OPTIONS.map((s) => (
                    <button key={s.id} type="button"
                      onClick={(e) => { e.stopPropagation(); toggleSegmentPill(s.id); }}
                      title={`${s.label}: ${kpis.segments[s.id] || 0} — click to filter`}
                      data-testid={`kpi-mix-${s.id}`}
                      className={`text-[9px] font-semibold rounded-full px-1.5 py-0.5 border transition-shadow ${WS_SEGMENT_TONE[s.id]} ${segmentFilter.includes(s.id) ? 'ring-1 ring-[#0F2040]' : ''}`}>
                      {kpis.segments[s.id] || 0} {WS_SEGMENT_SHORT[s.id]}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${statusParts.includes('draft') ? 'ring-2 ring-slate-400' : ''}`} onClick={() => handleFilterChange('draft')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-slate-600 font-['Outfit']" data-testid="kpi-draft">{kpis.draft || 0}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Draft</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${statusParts.includes('in_review') ? 'ring-2 ring-amber-400' : ''}`} onClick={() => handleFilterChange('in_review')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600 font-['Outfit']" data-testid="kpi-in-review">{kpis.in_review || 0}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />In Review</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${statusParts.includes('approved') ? 'ring-2 ring-emerald-400' : ''}`} onClick={() => handleFilterChange('approved')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-emerald-600 font-['Outfit']" data-testid="kpi-approved">{kpis.approved || 0}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Approved</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${statusParts.includes('changes_requested') ? 'ring-2 ring-rose-400' : ''}`} onClick={() => handleFilterChange('changes_requested')}>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-rose-600 font-['Outfit']" data-testid="kpi-changes">{kpis.changes_requested || 0}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" />Changes Req.</p>
            </CardContent>
          </Card>
          <Card
            className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${hasDocuments === 'no' ? 'ring-2 ring-amber-400' : ''}`}
            onClick={() => { setHasDocuments(hasDocuments === 'no' ? '' : 'no'); setPage(1); }}
            data-testid="kpi-missing-docs-card"
            title="Estimates with no files or links attached — click to filter"
          >
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600 font-['Outfit']" data-testid="kpi-missing-docs">{kpis.missing_documents || 0}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1"><Paperclip className="w-2.5 h-2.5 text-amber-500" />Missing Docs</p>
            </CardContent>
          </Card>
          <Card
            className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${lockedFilter === 'true' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => { setLockedFilter(lockedFilter === 'true' ? '' : 'true'); setPage(1); }}
            data-testid="kpi-locked-card"
            title="Estimates with effort & cost locked — click to filter"
          >
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-700 font-['Outfit']" data-testid="kpi-locked">{kpis.locked || 0}</p>
              <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1"><Lock className="w-2.5 h-2.5 text-amber-600" />Locked</p>
            </CardContent>
          </Card>
          {slipSummary && (
            <Card
              className={`border-slate-200 hover:shadow-sm transition-shadow cursor-pointer ${showSlip ? 'ring-2 ring-[#4ABFCF]' : ''}`}
              onClick={() => setShowSlip(v => !v)}
              data-testid="kpi-wave-slip-card"
              title={showSlip ? 'Hide the portfolio wave slip detail' : 'Show the portfolio wave slip detail'}
            >
              <CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold font-['Outfit'] ${slipSummary.over_threshold > 0 ? 'text-rose-600' : slipSummary.late_waves > 0 ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="kpi-wave-slip">
                  {slipSummary.over_threshold > 0 ? slipSummary.over_threshold : (slipSummary.late_waves || 0)}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5 flex items-center justify-center gap-1">
                  <TrendingDown className={`w-2.5 h-2.5 ${slipSummary.over_threshold > 0 ? 'text-rose-500' : slipSummary.late_waves > 0 ? 'text-amber-500' : 'text-emerald-500'}`} />
                  Wave Slip{slipSummary.over_threshold > 0 ? ` · over ${slipSummary.threshold_days}d` : slipSummary.late_waves > 0 ? ' · late' : ' · on track'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Portfolio wave slip — expanded detail, toggled from the KPI tile */}
        {showSlip && <SlipRollupCard onOpenEstimate={(id) => navigate(`/planner?id=${id}&tab=waves`)} />}

        {/* Search + Filter Row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-2 flex-1 max-w-sm">
            <Input
              placeholder="Search estimates..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="text-xs"
              data-testid="workspace-search-input"
            />
            <Button variant="outline" size="sm" onClick={handleSearch} data-testid="workspace-search-btn">
              <Search className="w-3.5 h-3.5" />
            </Button>
            {search && (
              <Button variant="ghost" size="sm" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>Clear</Button>
            )}
            <SavedViewsMenu
              onApply={(savedView) => {
                const sp = new URLSearchParams(savedView.search || '');
                setSearchInput(sp.get('q') || '');
                setSearch(sp.get('q') || '');
                setStatusFilter(sp.get('status') || '');
                setShowArchived(sp.get('archived') === '1');
                setPresalesMode(sp.get('presales') === '1');
                setPage(Math.max(1, parseInt(sp.get('page'), 10) || 1));
                if (sp.get('view')) setViewModePersist(sp.get('view'));
                toast.success(`Loaded view: ${savedView.name}`);
              }}
              currentSearch={(() => {
                const sp = new URLSearchParams();
                if (search) sp.set('q', search);
                if (statusFilter) sp.set('status', statusFilter);
                if (showArchived) sp.set('archived', '1');
                if (presalesMode) sp.set('presales', '1');
                if (page > 1) sp.set('page', String(page));
                if (viewMode && viewMode !== sessionDefaultViewRef.current) sp.set('view', viewMode);
                return sp.toString();
              })()}
            />
          </div>
          <div className="flex items-center gap-1 ml-auto flex-wrap">
            <div className="flex items-center gap-1 mr-2" data-testid="order-by-controls">
              <ArrowUpDown className="w-3 h-3 text-slate-400" />
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                className="text-[10px] border border-slate-200 rounded px-1.5 py-1 bg-white text-slate-600"
                data-testid="order-by-select"
              >
                {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <button
                type="button"
                onClick={() => { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); setPage(1); }}
                className="text-[10px] font-semibold px-1.5 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                title={sortDir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
                data-testid="order-dir-toggle"
              >
                {sortDir === 'asc' ? '↑ A–Z' : '↓ Z–A'}
              </button>
              <button
                type="button"
                onClick={() => { setShowCompleted(v => !v); setPage(1); }}
                className={`text-[10px] font-medium px-2 py-1 rounded-full transition-all ${showCompleted ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                title={showCompleted ? 'Completed projects are shown — click to hide' : 'Completed projects are hidden — click to show'}
                data-testid="show-completed-toggle"
              >
                {showCompleted ? 'Hiding: none' : 'Hidden: Completed'}
              </button>
            </div>
            {/* List filters — mirrors the Portfolio Overview popover (Type / Workflow / Lifecycle) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-[11px] text-slate-600" data-testid="workspaces-list-filters-btn">
                  <Filter className="w-3 h-3 mr-1" /> Filters
                  {(statusParts.length + segmentFilter.length) > 0 && (
                    <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#4ABFCF] text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="workspaces-list-filters-count">{statusParts.length + segmentFilter.length}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[420px] p-3" data-testid="workspaces-list-filters-panel">
                <div className="space-y-2">
                  {WS_FILTER_GROUPS.map((g) => (
                    <div key={g.name}>
                      <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">{g.name}</div>
                      <div className="flex flex-wrap items-center gap-1">
                        {g.options.map((o) => (
                          <button key={o.id} type="button" onClick={() => toggleListFilter(g.type, o.id)}
                            data-testid={`ws-filter-${o.id}`}
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                              (g.type === 'segment' ? segmentFilter.includes(o.id) : statusParts.includes(o.id))
                                ? 'bg-[#0F2040] text-white border-[#0F2040]'
                                : 'border-slate-300 text-slate-600 hover:border-slate-400'
                            }`}>{o.label}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(statusParts.length + segmentFilter.length) > 0 && (
                    <button onClick={() => { setStatusFilter(''); setSegmentFilter([]); setPage(1); }} className="text-[10px] text-slate-400 underline px-1" data-testid="ws-filter-clear">clear all</button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedIds.size > 0 && (
          <div className="sticky top-0 z-30 bg-[#0F2040] rounded-xl p-3 flex items-center justify-between shadow-lg animate-in slide-in-from-bottom-2" data-testid="bulk-action-bar">
            <div className="flex items-center gap-3">
              <button onClick={() => setSelectedIds(new Set())} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
              <span className="text-xs text-white font-medium">{selectedIds.size} selected</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white/10 text-white hover:bg-white/20 border-0"
                onClick={() => setShowSimModal(true)}
                data-testid="bulk-simulate-btn"
              >
                <Play className="w-3.5 h-3.5 mr-1" /> Simulate
              </Button>
              <Button
                size="sm"
                className="bg-[#4ABFCF] hover:bg-[#3AAAB9] text-white"
                onClick={() => setShowSendModal(true)}
                data-testid="bulk-send-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Send Quotes
              </Button>
              {/* Bulk Set Status — opens a modal with the full
                  workflow/lifecycle picker + "Clear override" so
                  leadership can bulk-approve, bulk-confirm, bulk-mark
                  Won/Lost, or bulk-archive in one shot. Destructive
                  picks (Won/Lost/Archived) get a warning preview before
                  apply. Visible in both active and archived views. */}
              <Button
                size="sm"
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/15 border border-transparent hover:border-white/30"
                onClick={() => setShowSetStatusModal(true)}
                data-testid="bulk-set-status-btn"
                title="Set workflow status across selected estimates"
              >
                <Tag className="w-3.5 h-3.5 mr-1" /> Set Status…
              </Button>
              {/* Bulk Export PDF — fans out client-side Scope Pack PDF
                  generation per selected row. Visible in BOTH active and
                  archived views since reps may want a PDF of an archived
                  estimate for record-keeping. */}
              <Button
                size="sm"
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/15 border border-transparent hover:border-white/30"
                onClick={() => bulkExportPdf()}
                disabled={bulkExporting}
                data-testid="bulk-export-pdf-btn"
                title="Export each selected estimate as a Scope Pack PDF"
              >
                {bulkExporting
                  ? <span className="w-3.5 h-3.5 mr-1 inline-block border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <FileDown className="w-3.5 h-3.5 mr-1" />}
                Export PDFs
              </Button>
              {/* Bulk Archive — soft archive, mirrors the per-row Archive
                  icon. Only mounted in the active view (Archived view
                  shows Restore Selected instead). */}
              {!showArchived && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-amber-200 hover:text-amber-100 hover:bg-amber-500/15 border border-transparent hover:border-amber-400/40"
                  onClick={() => bulkArchiveSelected()}
                  disabled={bulkArchiving}
                  data-testid="bulk-archive-btn"
                  title="Archive selected estimates (soft — can be restored)"
                >
                  {bulkArchiving
                    ? <span className="w-3.5 h-3.5 mr-1 inline-block border-2 border-amber-200/30 border-t-amber-200 rounded-full animate-spin" />
                    : <Archive className="w-3.5 h-3.5 mr-1" />}
                  Archive Selected
                </Button>
              )}
              {/* Bulk Restore — only mounted in the Archived view. Mirrors
                  the per-row ArchiveRestore icon but covers a whole batch
                  in one click. Emerald treatment so it visually pairs
                  with the per-row restore button. */}
              {showArchived && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-emerald-200 hover:text-emerald-100 hover:bg-emerald-500/15 border border-transparent hover:border-emerald-400/40"
                  onClick={() => bulkRestoreSelected()}
                  disabled={bulkRestoring}
                  data-testid="bulk-restore-btn"
                  title="Restore selected estimates back to active"
                >
                  {bulkRestoring ? (
                    <span className="w-3.5 h-3.5 mr-1 inline-block border-2 border-emerald-200/30 border-t-emerald-200 rounded-full animate-spin" />
                  ) : (
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1" />
                  )}
                  Restore Selected
                </Button>
              )}
              {/* Bulk Delete — Super Admin only. Sits at the right end of
                  the bulk-action toolbar with red treatment so it's hard
                  to miss but visually distinct from the safe actions on
                  its left. Confirms once before firing because it's
                  irreversible (cascades versions + saved plans). */}
              {user?.role === 'super_admin' && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-300 hover:text-red-200 hover:bg-red-500/15 border border-transparent hover:border-red-400/40"
                  onClick={() => bulkDeleteSelected()}
                  disabled={bulkDeleting}
                  data-testid="bulk-delete-btn"
                  title="Permanently delete selected estimates (cascades versions + plans)"
                >
                  {bulkDeleting ? (
                    <span className="w-3.5 h-3.5 mr-1 inline-block border-2 border-red-300/30 border-t-red-300 rounded-full animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                  )}
                  Delete Selected
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Select All Toggle */}
        {!loading && workspaces.length > 0 && (
          <div className="flex items-center gap-2">
            <button onClick={toggleSelectAll} className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-[#0F2040] transition-colors" data-testid="select-all-btn">
              {selectedIds.size === workspaces.length
                ? <CheckSquare className="w-3.5 h-3.5 text-[#4ABFCF]" />
                : <Square className="w-3.5 h-3.5" />}
              {selectedIds.size === workspaces.length ? 'Deselect all' : 'Select all'}
            </button>
            <span className="text-[9px] text-slate-400">({total} total)</span>
          </div>
        )}

        {/* Workspace Cards */}
        {loading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
          </div>
        ) : workspaces.length === 0 ? (
          <Card className="border-slate-200 relative overflow-hidden" data-testid="workspaces-empty-state">
            <img
              src="/seertech-mark.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none select-none absolute -right-8 -bottom-8 w-48 h-48 opacity-[0.06]"
            />
            <CardContent className="p-12 text-center relative">
              <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                {search || statusFilter ? 'No workspaces match your filters.' : 'No estimates yet. Create your first one!'}
              </p>
            </CardContent>
          </Card>
        ) : viewMode === 'list' ? (
          // ── LIST VIEW (default) — high-density rows, scannable at scale ──
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden divide-y divide-slate-100" data-testid="workspace-list">
            {workspaces.map((ws) => {
              const statusCfg = STATUS_CONFIG[ws.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusCfg.icon;
              const isSelected = selectedIds.has(ws.id);
              const displayName = dedupeName(ws.estimate_name);
              return (
                <div
                  key={ws.id}
                  className={`flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/60 transition-colors group ${isSelected ? 'bg-[#4ABFCF]/5' : ''}`}
                  data-testid={`workspace-row-${ws.id}`}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelect(ws.id); }}
                    className="text-slate-300 hover:text-[#4ABFCF] transition-colors shrink-0"
                    data-testid={`select-${ws.id}`}
                  >
                    {isSelected
                      ? <CheckSquare className="w-4 h-4 text-[#4ABFCF]" />
                      : <Square className="w-4 h-4" />}
                  </button>
                  <EstimateStatusSelect
                    estimateId={ws.id}
                    currentStatus={ws.status}
                    overrideBy={ws.workflow_status_override_by_name}
                    overrideAt={ws.workflow_status_override_at}
                    onChange={() => fetchWorkspaces()}
                    testIdPrefix={`dashboard-status-${ws.id}`}
                    compact
                  />
                  <DealStageSelect estimateId={ws.id} stage={ws.deal_stage} lost={ws.deal_stage_lost} canEdit={ws.can_edit !== false} onChange={() => fetchWorkspaces()} testIdPrefix={`dashboard-stage-${ws.id}`} compact />
                  {ws.archived_at && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700 whitespace-nowrap" title="Archived estimates are hidden from active views, Insights and reports until restored" data-testid={`ws-archived-badge-${ws.id}`}>
                      <Archive className="w-3 h-3" /> Archived {String(ws.archived_at).slice(0, 10)}
                    </span>
                  )}
                  <button
                    onClick={() => navigate(getResumePath(ws))}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`workspace-name-${ws.id}`}
                    title={ws.estimate_name}
                  >
                    <div className="text-xs font-semibold text-[#0F2040] group-hover:text-[#4ABFCF] truncate flex items-center gap-1.5">
                      <span className="truncate">{displayName}</span>
                      {(ws.versions_count || 0) > 1 && (
                        <span
                          className="text-[9px] font-bold text-blue-700 bg-blue-100 border border-blue-200 px-1.5 py-0 rounded shrink-0"
                          title={`${ws.versions_count} revisions on file (current: v${ws.version_number})`}
                          data-testid={`ws-version-${ws.id}`}
                        >
                          v{ws.version_number}
                        </span>
                      )}
                      {ws.locked && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0 rounded shrink-0"
                          title={`Effort & cost locked${ws.locked_by ? ` by ${ws.locked_by}` : ''}${ws.locked_at ? ` on ${new Date(ws.locked_at).toLocaleDateString()}` : ''}`}
                          data-testid={`ws-locked-${ws.id}`}
                        >
                          <Lock className="w-2.5 h-2.5" /> Locked
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mt-0.5">
                      {ws.organization_name && (
                        <span className="flex items-center gap-0.5"><Building2 className="w-2.5 h-2.5" />{ws.organization_name}</span>
                      )}
                      {ws.implementation_type && (
                        <span className="text-slate-400">· {ws.implementation_type.replace(/_/g, ' ')}</span>
                      )}
                      {ws.implementation_type === 'Project' ? (
                        <span className={`text-[9px] font-semibold rounded-full px-1.5 py-0.5 border ${ws.project_kind === 'rnd' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}
                          title={ws.project_kind === 'rnd' ? 'Seertech internal R&D project' : 'Customer project (non-implementation)'}
                          data-testid={`ws-project-kind-${ws.id}`}>
                          {ws.project_kind === 'rnd' ? 'Seertech R&D' : 'Customer Project'}
                        </span>
                      ) : ws.implementation_type ? (
                        <span className="text-[9px] font-semibold rounded-full px-1.5 py-0.5 border bg-sky-50 text-sky-700 border-sky-200"
                          title="Implementation estimate"
                          data-testid={`ws-segment-${ws.id}`}>
                          Implementation
                        </span>
                      ) : null}
                      {(ws.region || ws.contracting_entity_id) && (
                        <span className="text-[9px] font-semibold rounded-full px-1.5 py-0.5 border bg-teal-50 text-teal-700 border-teal-200"
                          title={`Entity ${(ws.contracting_entity_id || '').toUpperCase()}${ws.region ? ` · Region ${ws.region}` : ''}`}
                          data-testid={`ws-region-${ws.id}`}>
                          {ws.region || (ws.contracting_entity_id || '').toUpperCase()}
                        </span>
                      )}
                      {(ws.complexity_label || ws.complexity) && (
                        <span className="text-slate-400">· {ws.complexity_label || ws.complexity}</span>
                      )}
                      {ws.updated_by_name && (
                        <span className="text-slate-400 truncate" title={`Last updated by ${ws.updated_by_name}`}>· by {ws.updated_by_name}</span>
                      )}
                      {ws.sales_lead?.name && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-medium border border-[#4ABFCF]/30 bg-[#4ABFCF]/10 text-[#0F2040]"
                          title={`Sales Lead: ${ws.sales_lead.name}`}
                          data-testid={`ws-sales-lead-chip-${ws.id}`}
                        >
                          {ws.sales_lead.name}
                        </span>
                      )}
                      {ws.cs_lead?.name && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-medium border border-emerald-300 bg-emerald-50 text-emerald-800"
                          title={`CS Lead: ${ws.cs_lead.name}`}
                          data-testid={`ws-cs-lead-chip-${ws.id}`}
                        >
                          CS · {ws.cs_lead.name}
                        </span>
                      )}
                      {(() => {
                        // Discount sign-off audit chip — click opens the full timeline.
                        const pct = ws.rate_card_blanket_discount_pct;
                        if (!(pct > 0)) return null;
                        const appr = ws.discount_approved;
                        const rej = ws.discount_rejected;
                        let cls, label, tid;
                        if (appr && appr.pct === pct) {
                          cls = 'border-emerald-300 bg-emerald-50 text-emerald-700';
                          label = `${pct}% disc approved`; tid = `ws-discount-approved-${ws.id}`;
                        } else if (rej && rej.pct === pct) {
                          cls = 'border-rose-300 bg-rose-50 text-rose-700';
                          label = `${pct}% disc rejected`; tid = `ws-discount-rejected-${ws.id}`;
                        } else if (ws.discount_alerted_pct === pct) {
                          cls = 'border-amber-300 bg-amber-50 text-amber-700';
                          label = `${pct}% disc awaiting sign-off`; tid = `ws-discount-pending-${ws.id}`;
                        } else return null;
                        return (
                          <span role="button" tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); setSignoffWs(ws); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setSignoffWs(ws); } }}
                            className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-medium border cursor-pointer hover:opacity-80 ${cls}`}
                            title="Click for the full discount sign-off timeline"
                            data-testid={tid}>
                            {label}
                          </span>
                        );
                      })()}
                      {ws.win_hint && (
                        <span
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-medium border ${
                            ws.win_hint.level === 'high' ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                              : ws.win_hint.level === 'moderate' ? 'border-amber-300 bg-amber-50 text-amber-700'
                                : 'border-rose-300 bg-rose-50 text-rose-700'}`}
                          title={`Win likelihood ${ws.win_hint.level} — ${ws.win_hint.wins} of ${ws.win_hint.wins + ws.win_hint.losses} past pursuits for this client won${ws.win_hint.caution ? ' (tempered by the pricing-risk flag)' : ''}`}
                          data-testid={`ws-win-hint-${ws.id}`}
                        >
                          Win {ws.win_hint.pct}%
                        </span>
                      )}
                      <ResumeChip estimate={ws} compact />
                      {ws.source_docs?.total > 0 && (() => {
                        const { files = 0, links = 0, total = 0 } = ws.source_docs;
                        const parts = [];
                        if (files) parts.push(`${files} file${files === 1 ? '' : 's'}`);
                        if (links) parts.push(`${links} link${links === 1 ? '' : 's'}`);
                        return (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0 rounded-full text-[9px] font-medium border border-slate-200 bg-slate-50 text-slate-600"
                            title={`Supporting material: ${parts.join(' · ')}`}
                            data-testid={`ws-source-docs-chip-${ws.id}`}
                          >
                            <Paperclip className="w-2.5 h-2.5" />
                            {total}
                          </span>
                        );
                      })()}
                      {presalesMode && ws.has_requirements && (() => {
                        // Pre-Sales drift indicator: requirements doc was
                        // updated AFTER the last /apply (or never applied).
                        const u = ws.requirements_updated_at;
                        const a = ws.requirements_applied_at;
                        const drift = u && (!a || new Date(u) > new Date(a));
                        return drift ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0 rounded-full bg-amber-50 text-amber-700 border border-amber-200 ml-1"
                            data-testid={`presales-drift-chip-${ws.id}`}
                            title={a
                              ? `Requirements updated ${new Date(u).toLocaleDateString()} — last applied ${new Date(a).toLocaleDateString()}. Re-open Requirements & click Apply.`
                              : 'Requirements captured but never applied to the Estimator.'}
                          >
                            ! out of sync
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1"
                            data-testid={`presales-applied-chip-${ws.id}`}
                            title={`Applied ${new Date(a).toLocaleDateString()}`}
                          >
                            ✓ in sync
                          </span>
                        );
                      })()}
                    </div>
                  </button>
                  {ws.product_year1 != null && (<>
                    <div className="hidden lg:block text-right shrink-0" data-testid={`ws-product-y1-${ws.id}`} title={`${ws.product_package || 'Subscription'} · ${ws.product_term_years}-yr term`}>
                      <div className="text-[9px] text-slate-400 uppercase tracking-wide">Product / yr</div>
                      <div className="text-xs font-semibold text-violet-700">{getCurrencySymbol(ws.product_currency)}{Math.round(ws.product_year1).toLocaleString()}</div>
                    </div>
                    <div className="hidden lg:block text-right shrink-0" data-testid={`ws-product-tco-${ws.id}`} title={`Total subscription value over ${ws.product_term_years} years`}>
                      <div className="text-[9px] text-slate-400 uppercase tracking-wide">Product TCO</div>
                      <div className="text-xs font-semibold text-violet-700">{getCurrencySymbol(ws.product_currency)}{Math.round(ws.product_tco).toLocaleString()}<span className="text-[9px] text-slate-400 font-normal ml-0.5">/{ws.product_term_years}y</span></div>
                    </div>
                  </>)}
                  <div className="hidden lg:block text-right shrink-0">
                    <div className="text-[9px] text-slate-400 uppercase tracking-wide">Impl. Effort</div>
                    <div className="text-xs font-semibold text-[#0F2040]">{ws.total_effort?.toLocaleString() || 0} hrs</div>
                  </div>
                  <div className="hidden lg:block text-right shrink-0">
                    <div className="text-[9px] text-slate-400 uppercase tracking-wide">Impl. Cost</div>
                    <div className="text-xs font-semibold text-[#4ABFCF]">{getCurrencySymbol(ws.currency_code)}{ws.total_cost?.toLocaleString() || 0}</div>
                  </div>
                  <span className="hidden md:inline text-[10px] text-slate-400 flex items-center gap-1 shrink-0 w-16 text-right">
                    {timeAgo(ws.last_activity)}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={(e) => togglePin(ws.id, e)}
                      disabled={pinningId === ws.id}
                      className={`p-1 rounded transition-colors ${
                        pinnedIdSet.has(ws.id)
                          ? 'text-amber-500 hover:text-amber-700'
                          : 'text-slate-300 hover:text-amber-500'
                      }`}
                      data-testid={`pin-toggle-${ws.id}`}
                      title={pinnedIdSet.has(ws.id) ? 'Unpin' : 'Pin to top'}
                    >
                      {pinnedIdSet.has(ws.id)
                        ? <Star className="w-3.5 h-3.5 fill-current" />
                        : <StarOff className="w-3.5 h-3.5" />}
                    </button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-[#4ABFCF]" onClick={() => navigate(getResumePath(ws))} title="Open" data-testid={`ws-open-${ws.id}`}>
                      <FolderOpen className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-[#4ABFCF]" onClick={() => navigate(`/workspace/${ws.id}/quote`)} title="Quote" data-testid={`ws-quote-${ws.id}`}>
                      <Briefcase className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-[#0F2040]" onClick={() => navigate(`/estimator?load=${ws.id}`)} title="Edit" data-testid={`ws-edit-${ws.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    {showArchived ? (
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => restoreWorkspace(ws.id)} title={`Restore to active workspaces${ws.archived_at ? ` (archived ${String(ws.archived_at).slice(0, 10)})` : ''}`} data-testid={`ws-restore-${ws.id}`}>
                        <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-amber-600" onClick={() => archiveWorkspace(ws.id)} title="Archive (hides from active views; can be restored)" data-testid={`ws-archive-${ws.id}`}>
                        <Archive className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {(user?.role === 'super_admin' || ws.user_id === user?.id) && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" onClick={() => deleteWorkspace(ws.id)} title="Permanently delete (cascades versions + plans)" data-testid={`ws-delete-${ws.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="workspace-grid">
            {workspaces.map((ws) => {
              const statusCfg = STATUS_CONFIG[ws.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusCfg.icon;
              const isSelected = selectedIds.has(ws.id);
              const displayName = dedupeName(ws.estimate_name);
              return (
                <Card
                  key={ws.id}
                  className={`border-slate-200 hover:shadow-md transition-all cursor-pointer group ${
                    isSelected ? 'ring-2 ring-[#4ABFCF] border-[#4ABFCF]/40' : 'hover:border-[#4ABFCF]/30'
                  }`}
                  data-testid={`workspace-card-${ws.id}`}
                >
                  <CardContent className="p-4">
                    {/* Checkbox + status + activity */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleSelect(ws.id); }}
                          className="text-slate-300 hover:text-[#4ABFCF] transition-colors"
                          data-testid={`select-${ws.id}`}
                        >
                          {isSelected
                            ? <CheckSquare className="w-4 h-4 text-[#4ABFCF]" />
                            : <Square className="w-4 h-4" />}
                        </button>
                        <EstimateStatusSelect
                          estimateId={ws.id}
                          currentStatus={ws.status}
                          overrideBy={ws.workflow_status_override_by_name}
                          overrideAt={ws.workflow_status_override_at}
                          onChange={() => fetchWorkspaces()}
                          testIdPrefix={`dashboard-status-${ws.id}`}
                          compact
                        />
                        <DealStageSelect estimateId={ws.id} stage={ws.deal_stage} lost={ws.deal_stage_lost} canEdit={ws.can_edit !== false} onChange={() => fetchWorkspaces()} testIdPrefix={`dashboard-stage-${ws.id}`} compact />
                        {ws.archived_at && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`ws-card-archived-badge-${ws.id}`}>
                            <Archive className="w-3 h-3" /> Archived {String(ws.archived_at).slice(0, 10)}
                          </span>
                        )}
                      </div>
                      <span className="text-[9px] text-slate-400 flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => togglePin(ws.id, e)}
                          disabled={pinningId === ws.id}
                          className={`p-0.5 rounded transition-colors ${
                            pinnedIdSet.has(ws.id)
                              ? 'text-amber-500 hover:text-amber-700'
                              : 'text-slate-300 hover:text-amber-500'
                          }`}
                          data-testid={`pin-toggle-${ws.id}`}
                          title={pinnedIdSet.has(ws.id) ? 'Unpin' : 'Pin to top'}
                        >
                          {pinnedIdSet.has(ws.id)
                            ? <Star className="w-3 h-3 fill-current" />
                            : <StarOff className="w-3 h-3" />}
                        </button>
                        <Activity className="w-2.5 h-2.5" />
                        {timeAgo(ws.last_activity)}
                      </span>
                    </div>

                    {/* Name */}
                    <h3
                      className="text-sm font-semibold text-[#0F2040] font-['Outfit'] truncate group-hover:text-[#4ABFCF] transition-colors cursor-pointer"
                      onClick={() => navigate(getResumePath(ws))}
                      data-testid={`workspace-name-${ws.id}`}
                      title={ws.estimate_name}
                    >
                      {displayName}
                    </h3>

                    {/* Meta badges */}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      {ws.organization_name && (
                        <span className="text-[9px] text-slate-500 flex items-center gap-0.5">
                          <Building2 className="w-2.5 h-2.5" /> {ws.organization_name}
                        </span>
                      )}
                      <Badge variant="outline" className="text-[8px] py-0 px-1.5">{ws.implementation_type?.replace('_', ' ')}</Badge>
                      <Badge variant="outline" className="text-[8px] py-0 px-1.5">{ws.complexity_label || ws.complexity}</Badge>
                      {ws.locked && (
                        <Badge className="text-[8px] py-0 px-1.5 bg-amber-100 text-amber-800 border-amber-300 inline-flex items-center gap-0.5" data-testid={`ws-locked-card-${ws.id}`}>
                          <Lock className="w-2.5 h-2.5" /> Locked
                        </Badge>
                      )}
                      <ResumeChip estimate={ws} />
                    </div>

                    {/* Metrics row */}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                      {ws.product_year1 != null && (<>
                        <div data-testid={`ws-card-product-y1-${ws.id}`} title={`${ws.product_package || 'Subscription'} · ${ws.product_term_years}-yr term`}>
                          <div className="flex items-center gap-0.5 text-slate-400 text-[8px]"><Package className="w-2.5 h-2.5" /> Product / yr</div>
                          <p className="text-xs font-bold text-violet-700 font-['Outfit']">{getCurrencySymbol(ws.product_currency)}{Math.round(ws.product_year1).toLocaleString()}</p>
                        </div>
                        <div data-testid={`ws-card-product-tco-${ws.id}`}>
                          <div className="flex items-center gap-0.5 text-slate-400 text-[8px]"><Package className="w-2.5 h-2.5" /> Product TCO</div>
                          <p className="text-xs font-bold text-violet-700 font-['Outfit']">{getCurrencySymbol(ws.product_currency)}{Math.round(ws.product_tco).toLocaleString()}<span className="text-[8px] text-slate-400 font-normal ml-0.5">/{ws.product_term_years}y</span></p>
                        </div>
                      </>)}
                      <div>
                        <div className="flex items-center gap-0.5 text-slate-400 text-[8px]"><Clock className="w-2.5 h-2.5" /> Impl. Effort</div>
                        <p className="text-xs font-bold text-[#0F2040] font-['Outfit']">{ws.total_effort?.toLocaleString() || 0} hrs</p>
                      </div>
                      <div>
                        <div className="flex items-center gap-0.5 text-slate-400 text-[8px]"><DollarSign className="w-2.5 h-2.5" /> Impl. Cost</div>
                        <p className="text-xs font-bold text-[#4ABFCF] font-['Outfit']">{getCurrencySymbol(ws.currency_code)}{ws.total_cost?.toLocaleString() || 0}</p>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        {ws.comment_count > 0 && (
                          <span className="text-[9px] text-slate-400 flex items-center gap-0.5" title="Comments">
                            <MessageSquare className="w-2.5 h-2.5" /> {ws.comment_count}
                          </span>
                        )}
                        {ws.component_count > 0 && (
                          <span className="text-[9px] text-slate-400 flex items-center gap-0.5" title="Components">
                            <BarChart3 className="w-2.5 h-2.5" /> {ws.component_count}
                          </span>
                        )}
                      </div>
                    </div>

                    <ApprovalBar approval={ws.approval} />

                    {/* Action row */}
                    <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100">
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500 hover:text-[#4ABFCF] px-2" onClick={(e) => { e.stopPropagation(); navigate(getResumePath(ws)); }} data-testid={`ws-open-${ws.id}`}>
                        <FolderOpen className="w-3 h-3 mr-1" /> Open
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500 hover:text-[#4ABFCF] px-2" onClick={(e) => { e.stopPropagation(); navigate(`/workspace/${ws.id}/quote`); }} data-testid={`ws-quote-${ws.id}`}>
                        <Briefcase className="w-3 h-3 mr-1" /> Quote
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500 hover:text-[#0F2040] px-2 ml-auto" onClick={(e) => { e.stopPropagation(); navigate(`/estimator?load=${ws.id}`); }} data-testid={`ws-edit-${ws.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 pt-4" data-testid="workspace-pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="workspace-prev">
              <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
            </Button>
            <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="workspace-next">
              Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}
      </main>

      {/* Modals */}
      {showSendModal && <BulkSendModal selectedIds={selectedIds} workspaces={workspaces} onClose={() => setShowSendModal(false)} onDone={handleModalDone} />}
      {showSimModal && <SimulateModal selectedIds={selectedIds} onClose={() => setShowSimModal(false)} onDone={handleModalDone} />}
      {showSetStatusModal && <BulkSetStatusModal selectedIds={selectedIds} workspaces={workspaces} onClose={() => setShowSetStatusModal(false)} onDone={handleModalDone} />}
    </div>
  );
}
